import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { update } from '../../../services/item.js';
import { uploadPic, uploadPicFromUrl } from '../../../services/pic.js';
import { setBoardViewport, setDrawerOpen, setOpenModales } from '../../../store/global.js';
import { saveAll } from '../../../store/items.js';
import store from '../../../store/store.js';
import { deepCloneArray } from '../../../utils/array.js';
import { canAccessItem, getData, getId } from '../../../utils/item.js';
import { attachNodeToGroups } from '../boardDrawAdditions.js';
import { normalizeMapState } from './boardMapDefaults.js';
import { BoardMapBackgroundLayer } from './boardMapBackgroundLayer.js';
import { BoardMapGridLayer } from './boardMapGridLayer.js';
import { BoardMapLinksLayer } from './boardMapLinksLayer.js';
import { BoardMapMeasurementsLayer } from './boardMapMeasurementsLayer.js';
import { BoardMapGroupsLayer } from './boardMapGroupsLayer.js';
import { BoardMapPinsLayer } from './boardMapPinsLayer.js';
import { BoardMapShapesLayer } from './boardMapShapesLayer.js';
import { BoardMapRegionsLayer } from './boardMapRegionsLayer.js';
import { BoardMapDrawingsLayer } from './boardMapDrawingsLayer.js';
import { BoardMapWidgetsLayer } from './boardMapWidgetsLayer.js';
import { BoardMapDraftLayer } from './boardMapDraftLayer.js';
import { BoardMapMiniMap } from './boardMapMiniMap.js';
import { BoardCanvasRoot } from '../boardCanvasRoot.js';
import { getDefaultMapWidgetLayout } from './boardMapWidgetLayout.js';
import { useBoardCanvasHotkeys } from '../../../hooks/useBoardCanvasHotkeys.js';
import { useBoardDeleteKey } from '../../../hooks/useBoardDeleteKey.js';
import { useBoardEffectiveInteractionMode } from '../../../hooks/useBoardEffectiveInteractionMode.js';
import { useBoardSelectionBox } from '../../../hooks/useBoardSelectionBox.js';
import { useBoardMapViewport } from '../../../hooks/useBoardMapViewport.js';
import { useBoardMapSelection } from '../../../hooks/useBoardMapSelection.js';
import { useBoardMapPersistence } from '../../../hooks/useBoardMapPersistence.js';
import { useBoardMapInteractions } from '../../../hooks/useBoardMapInteractions.js';
import { useBoardSelectionSync } from '../../../hooks/useBoardSelectionSync.js';
import { useBoardMapEntityCommands } from '../../../hooks/useBoardMapEntityCommands.js';
import { useBoardMapExternalDrop } from '../../../hooks/useBoardMapExternalDrop.js';
import { useBoardCommandTick } from '../../../hooks/useBoardCommandTick.js';
import { useBoardHistory } from '../../../hooks/useBoardHistory.js';
import { BoardMapModals } from './boardMapModals.js';
import {
  getMapTargetNodeId,
  parseMapNodeId,
  toMapDrawingNodeId,
  toMapGroupNodeId,
  toMapPinNodeId,
  toMapRegionNodeId,
  toMapShapeNodeId,
  toMapWidgetNodeId,
} from './boardMapNodeIds.js';
import { getLinkGeometry } from '../boardDrawLinkGeometry.js';

const REGION_SNAP_SCREEN_DISTANCE = 12;
const MEASURE_ENDPOINT_SNAP_SCREEN_DISTANCE = 14;
const getPairKey = (a, b) => {
  const left = String(a || '');
  const right = String(b || '');
  return left < right ? `${left}::${right}` : `${right}::${left}`;
};

const cloneHistoryValue = value => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (error) {
      // fallback below
    }
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const getImageMeta = url =>
  new Promise((resolve, reject) => {
    if (!url) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () =>
      resolve({
        width: img.naturalWidth || 0,
        height: img.naturalHeight || 0,
      });
    img.onerror = reject;
    img.src = url;
  });

const projectPointToSegment = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return {
      x: start.x,
      y: start.y,
      distance: Math.hypot(point.x - start.x, point.y - start.y),
    };
  }
  const t = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared),
  );
  const x = start.x + dx * t;
  const y = start.y + dy * t;
  return {
    x,
    y,
    distance: Math.hypot(point.x - x, point.y - y),
  };
};

const getSnappedRegionPoint = ({
  point,
  regions = [],
  zoom = 1,
  excludeRegionId = null,
  excludePointIndex = null,
}) => {
  const snapDistance = REGION_SNAP_SCREEN_DISTANCE / Math.max(zoom || 1, 0.1);
  let bestPoint = point;
  let bestDistance = snapDistance;

  regions.forEach(region => {
    const regionPoints = Array.isArray(region?.points) ? region.points : [];
    const isExcludedRegion = excludeRegionId && region?.id === excludeRegionId;

    regionPoints.forEach((candidatePoint, candidateIndex) => {
      if (isExcludedRegion && candidateIndex === excludePointIndex) {
        return;
      }
      if (
        !Number.isFinite(candidatePoint?.x) ||
        !Number.isFinite(candidatePoint?.y)
      ) {
        return;
      }
      const distance = Math.hypot(point.x - candidatePoint.x, point.y - candidatePoint.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestPoint = { x: candidatePoint.x, y: candidatePoint.y };
      }
    });

    if (regionPoints.length < 2 || isExcludedRegion) {
      return;
    }

    regionPoints.forEach((segmentStart, index) => {
      const segmentEnd = regionPoints[(index + 1) % regionPoints.length];
      if (!segmentEnd) {
        return;
      }
      const projectedPoint = projectPointToSegment(point, segmentStart, segmentEnd);
      if (projectedPoint.distance < bestDistance) {
        bestDistance = projectedPoint.distance;
        bestPoint = { x: projectedPoint.x, y: projectedPoint.y };
      }
    });
  });

  return {
    x: Math.round(bestPoint.x),
    y: Math.round(bestPoint.y),
  };
};

const getMeasurementPoints = measurement => {
  const savedPoints = Array.isArray(measurement?.points)
    ? measurement.points
        .filter(point => Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)))
        .map(point => ({
          x: Number(point.x),
          y: Number(point.y),
        }))
    : [];
  if (savedPoints.length >= 2) {
    return savedPoints;
  }
  const x1 = Number(measurement?.x1);
  const y1 = Number(measurement?.y1);
  const x2 = Number(measurement?.x2);
  const y2 = Number(measurement?.y2);
  if ([x1, y1, x2, y2].every(Number.isFinite)) {
    return [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
  }
  return [];
};

const computeMeasurementLength = points => {
  if (!Array.isArray(points) || points.length < 2) {
    return 0;
  }
  return points.slice(1).reduce((sum, point, index) => {
    const previous = points[index];
    return sum + Math.hypot(Number(point.x) - Number(previous.x), Number(point.y) - Number(previous.y));
  }, 0);
};

const findMeasureExtensionTarget = ({ measurements = [], startPoint, zoom = 1 }) => {
  if (!startPoint || !Number.isFinite(startPoint.x) || !Number.isFinite(startPoint.y)) {
    return null;
  }
  const maxDistance = MEASURE_ENDPOINT_SNAP_SCREEN_DISTANCE / Math.max(zoom || 1, 0.1);
  let best = null;

  measurements.forEach(measurement => {
    const points = getMeasurementPoints(measurement);
    if (points.length < 2) {
      return;
    }
    const firstPoint = points[0];
    const lastPoint = points[points.length - 1];
    const startDistance = Math.hypot(startPoint.x - firstPoint.x, startPoint.y - firstPoint.y);
    const endDistance = Math.hypot(startPoint.x - lastPoint.x, startPoint.y - lastPoint.y);
    if (startDistance <= maxDistance && (!best || startDistance < best.distance)) {
      best = { measurementId: measurement?.id, side: 'start', distance: startDistance };
    }
    if (endDistance <= maxDistance && (!best || endDistance < best.distance)) {
      best = { measurementId: measurement?.id, side: 'end', distance: endDistance };
    }
  });

  return best;
};

const isMapElementTarget = target =>
  target instanceof Element &&
  Boolean(
    target.closest(
      [
        '[data-map-widget-root="true"]',
        '[data-map-link-root="true"]',
        '[data-map-link-control-handle="true"]',
        '[data-map-link-label-editor="true"]',
        '[data-map-shape-root="true"]',
        '[data-map-region-root="true"]',
        '[data-map-drawing-root="true"]',
        '[data-map-shape-resize-handle="true"]',
        'button',
        'input',
        'textarea',
        '[contenteditable="true"]',
        '[data-map-group-root="true"]',
        'label',
      ].join(', '),
    ),
  );

const getStrokeBorderStyle = element => {
  if (!element) {
    return 'solid';
  }
  if (element?.strokeStyle) {
    return element.strokeStyle;
  }
  if (Number(element?.strokeWidth) >= 4) {
    return 'thick';
  }
  if (element?.strokeDasharray === '1 6') {
    return 'dotted';
  }
  if (element?.strokeDasharray) {
    return 'dashed';
  }
  if (Number(element?.strokeWidth) === 0) {
    return 'none';
  }
  return 'solid';
};

export const BoardMap = props => {
  const {
    itemId,
    boardId,
    boardWidth,
    editable = false,
    layoutEditable = editable,
    onZoomChange,
    onViewportChange,
    mapShapeType = 'circle',
    onPinSelectionChange,
    onLineSelectionChange,
    onSelectedLineColorChange,
    onSelectedLineArrowsChange,
    onSelectedLineBorderStyleChange,
    onSelectedPinColorChange,
    onSelectedPinSizeChange,
    onSelectedPinIconChange,
    onShapeSelectionChange,
    onShapeLayerSelectionChange,
    onSelectedShapeTypeChange,
    onSelectedShapeColorChange,
    onSelectedShapeBorderStyleChange,
    onRegionSelectionChange,
    onSelectedRegionColorChange,
    onSelectedRegionBorderStyleChange,
    onLinkActionChange,
    onDrawingSelectionChange,
    onSelectedDrawingColorChange,
    onSelectedDrawingBorderStyleChange,
    onCreateGroupAvailabilityChange,
    onEnterGroupAvailabilityChange,
    onUngroupAvailabilityChange,
    onInsideGroupChange,
    onActiveGroupIdChange,
    onActiveGroupLabelChange,
    onSelectionCountsChange,
    onMeasureModeChange,
    onMeasureScalesChange,
    onActiveMeasureScaleIdsChange,
    pinColorCommand = 'secondary',
    pinColorCommandTick,
    pinSizeCommand = 'md',
    pinSizeCommandTick,
    pinIconCommand = null,
    pinIconCommandTick,
    unlinkItemCommandTick,
    openLinkModalCommandTick,
    openBackgroundModalCommandTick,
    openGridSettingsModalCommandTick,
    toggleGridVisibleCommandTick,
    shapeTypeCommand = 'circle',
    shapeTypeCommandTick,
    shapeColorCommand = 'secondary',
    shapeColorCommandTick,
    shapeLayerCommand,
    shapeLayerCommandTick,
    regionColorCommand = 'secondary',
    regionColorCommandTick,
    regionBorderStyleCommand = 'solid',
    regionBorderStyleCommandTick,
    drawingColorCommand = 'secondary',
    drawingColorCommandTick,
    drawingBorderStyleCommand = 'solid',
    drawingBorderStyleCommandTick,
    lineColorCommand = 'secondary',
    lineColorCommandTick,
    lineArrowsCommand = 'none',
    lineArrowsCommandTick,
    groupCommand,
    groupCommandTick,
    toggleMeasureScaleCommand,
    toggleMeasureScaleCommandTick,
    zoomCommand,
    zoomCommandTick,
    interactionMode = 'pan',
    mapTool = 'select',
    measureMode = 'measure',
  } = props;

  const tab = useSelector(state => state.items[boardId]);
  const items = useSelector(state => state.items);
  const openModales = useSelector(state => state.global.openModales);
  const measureSettingsModalId = `map-measure-settings-${boardId}`;
  const pinLinkModalId = `map-pin-link-${boardId}`;
  const backgroundModalId = `map-background-${boardId}`;
  const gridSettingsModalId = `map-grid-settings-${boardId}`;
  const isAnyModalOpen = useMemo(
    () =>
      [
        measureSettingsModalId,
        pinLinkModalId,
        backgroundModalId,
        gridSettingsModalId,
      ].some(modalId => openModales?.[modalId] === true),
    [
      backgroundModalId,
      gridSettingsModalId,
      measureSettingsModalId,
      openModales,
      pinLinkModalId,
    ],
  );
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const backgroundFileInputRef = useRef(null);
  const panStateRef = useRef(null);
  const pinDragStateRef = useRef(null);
  const shapeDragStateRef = useRef(null);
  const shapeResizeStateRef = useRef(null);
  const pinClickTimeoutRef = useRef(null);
  const shapeClickTimeoutRef = useRef(null);
  const lineDraftRef = useRef(null);
  const previousMapToolRef = useRef(mapTool);
  const linkCurveDragStateRef = useRef(null);
  const selectedLinkLabelRef = useRef(null);
  const selectedGroupLabelRef = useRef(null);
  const drawingStateRef = useRef(null);
  const shapeDraftRef = useRef(null);
  const widgetDragStateRef = useRef(null);
  const widgetResizeStateRef = useRef(null);
  const regionDragStateRef = useRef(null);
  const regionPointDragStateRef = useRef(null);
  const drawingDragStateRef = useRef(null);
  const regionClickTimeoutRef = useRef(null);
  const previousViewportRef = useRef(null);
  const selectionDragStateRef = useRef(null);
  const regionDrawStateRef = useRef(null);
  const groupDragStateRef = useRef(null);
  const didPanRef = useRef(false);
  const didPinDragRef = useRef(null);
  const didShapeDragRef = useRef(null);
  const didWidgetDragRef = useRef(null);
  const didSelectionDragRef = useRef(false);
  const didRegionDrawRef = useRef(false);
  const didRegionPointDragRef = useRef(false);
  const didDrawingRef = useRef(false);
  const lastHandledPinColorCommandTickRef = useRef(0);
  const lastHandledPinSizeCommandTickRef = useRef(0);
  const lastHandledPinIconCommandTickRef = useRef(0);
  const lastHandledUnlinkItemCommandTickRef = useRef(0);
  const lastHandledOpenLinkModalCommandTickRef = useRef(0);
  const lastHandledOpenBackgroundModalCommandTickRef = useRef(
    Number.isFinite(openBackgroundModalCommandTick)
      ? openBackgroundModalCommandTick
      : 0,
  );
  const lastHandledOpenGridSettingsModalCommandTickRef = useRef(
    Number.isFinite(openGridSettingsModalCommandTick)
      ? openGridSettingsModalCommandTick
      : 0,
  );
  const lastHandledToggleGridVisibleCommandTickRef = useRef(
    Number.isFinite(toggleGridVisibleCommandTick)
      ? toggleGridVisibleCommandTick
      : 0,
  );
  const lastHandledShapeTypeCommandTickRef = useRef(0);
  const lastHandledShapeColorCommandTickRef = useRef(0);
  const lastHandledShapeLayerCommandTickRef = useRef(0);
  const lastHandledRegionColorCommandTickRef = useRef(0);
  const lastHandledRegionBorderStyleCommandTickRef = useRef(0);
  const lastHandledDrawingColorCommandTickRef = useRef(0);
  const lastHandledDrawingBorderStyleCommandTickRef = useRef(0);
  const lastHandledLineColorCommandTickRef = useRef(0);
  const lastHandledLineArrowsCommandTickRef = useRef(0);
  const lastHandledGroupCommandTickRef = useRef(0);
  const lastHandledToggleMeasureScaleCommandTickRef = useRef(0);
  const mapState = useMemo(() => normalizeMapState(tab?.map), [tab?.map]);
  const { viewport, setViewport, onWheel } = useBoardMapViewport({
    containerRef,
    isAnyModalOpen,
    onZoomChange,
    onViewportChange: nextViewport => {
      onViewportChange?.(nextViewport);
      dispatch(setBoardViewport({ boardId, viewport: nextViewport }));
    },
    zoomCommand,
    zoomCommandTick,
    initialViewport: mapState?.viewport,
  });
  const {
    selectedPinId,
    selectedPinIds,
    selectedLinkId,
    selectedLinkIds,
    selectedShapeId,
    selectedShapeIds,
    selectedRegionId,
    selectedRegionIds,
    selectedDrawingId,
    selectedDrawingIds,
    selectedWidgetId,
    selectedWidgetIds,
    selectedGroupId,
    selectedGroupIds,
    clearSelection,
    selectPinOnly,
    selectLinkOnly,
    selectShapeOnly,
    selectRegionOnly,
    selectDrawingOnly,
    selectWidgetOnly,
    selectGroupOnly,
    setSelectionState,
  } = useBoardMapSelection();
  const { persistMapField: persistMapFieldRaw } = useBoardMapPersistence({ tab });
  const { isSpacePressed } = useBoardCanvasHotkeys();
  const { selectionBox, setSelectionBox, clearSelectionBox } = useBoardSelectionBox();
  const [backgroundUrlInput, setBackgroundUrlInput] = useState(
    mapState?.background?.url || '',
  );
  const [displayedBackground, setDisplayedBackground] = useState(mapState?.background);
  const [gridSizeInput, setGridSizeInput] = useState(
    String(mapState?.grid?.size || 50),
  );
  const [displayedGrid, setDisplayedGrid] = useState(mapState?.grid);
  const [isBackgroundSaving, setIsBackgroundSaving] = useState(false);
  const [backgroundError, setBackgroundError] = useState('');
  const [displayedPins, setDisplayedPins] = useState(mapState?.pins || []);
  const [displayedLinks, setDisplayedLinks] = useState(mapState?.links || []);
  const [displayedShapes, setDisplayedShapes] = useState(mapState?.shapes || []);
  const [displayedRegions, setDisplayedRegions] = useState(mapState?.regions || []);
  const [displayedDrawings, setDisplayedDrawings] = useState(mapState?.drawings || []);
  const [displayedMeasurements, setDisplayedMeasurements] = useState(
    mapState?.measurements || [],
  );
  const [measureScales, setMeasureScales] = useState(mapState?.measureScales || []);
  const [activeMeasureScaleIds, setActiveMeasureScaleIds] = useState(
    Array.isArray(mapState?.activeMeasureScaleIds) && mapState.activeMeasureScaleIds.length > 0
      ? mapState.activeMeasureScaleIds
      : mapState?.activeMeasureScaleId
        ? [mapState.activeMeasureScaleId]
        : [],
  );
  const [pendingMeasureReference, setPendingMeasureReference] = useState(null);
  const [measureScaleValueInput, setMeasureScaleValueInput] = useState('');
  const [measureScaleUnitInput, setMeasureScaleUnitInput] = useState('m');
  const [measureScaleLabelInput, setMeasureScaleLabelInput] = useState('');
  const [measureScaleError, setMeasureScaleError] = useState('');
  const [displayedGroups, setDisplayedGroups] = useState(mapState?.groups || []);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [shapeDraft, setShapeDraft] = useState(null);
  const [regionDraftPoints, setRegionDraftPoints] = useState([]);
  const [regionDraftHoverPoint, setRegionDraftHoverPoint] = useState(null);
  const [drawingDraftPoints, setDrawingDraftPoints] = useState([]);
  const [lineDraft, setLineDraft] = useState(null);
  const [widgetLayoutOverrides, setWidgetLayoutOverrides] = useState({});
  const isApplyingHistoryRef = useRef(false);
  const boardHistory = useBoardHistory({ limit: 50 });
  const effectiveInteractionMode = useBoardEffectiveInteractionMode({
    interactionMode,
    tool: mapTool,
    isSpacePressed,
    toolFallbackMode:
      mapTool === 'measure' && isSpacePressed ? 'pan' : 'tool',
  });
  const persistMapWidgetLayout = useCallback(
    async (widgetId, nextLayout, options = {}) => {
      const { skipHistory = false } = options || {};
      if (!tab?._id || !widgetId || !nextLayout) {
        return;
      }
      const latestTab = store.getState()?.items?.[tab._id] || tab;
      if (!Array.isArray(latestTab?.grid)) {
        return;
      }
      const nextGrid = deepCloneArray(latestTab.grid).map(gridObj => {
        const elementId = getId(gridObj);
        const payload = gridObj?.[elementId];
        if (elementId !== widgetId || !payload) {
          return gridObj;
        }
        return {
          [elementId]: {
            ...payload,
            map: {
              ...(payload?.map || {}),
              x: Math.round(Number(nextLayout?.x) || 0),
              y: Math.round(Number(nextLayout?.y) || 0),
              w: Math.max(80, Math.round(Number(nextLayout?.w) || 0)),
              h: Math.max(24, Math.round(Number(nextLayout?.h) || 0)),
            },
          },
        };
      });
      await update(`items/${tab._id}`, 'grid', nextGrid);
      if (!skipHistory && !isApplyingHistoryRef.current) {
        boardHistory.record({
          map: {
            pins: displayedPins,
            links: displayedLinks,
            shapes: displayedShapes,
            regions: displayedRegions,
            drawings: displayedDrawings,
            groups: displayedGroups,
            measurements: displayedMeasurements,
            measureScales,
            activeMeasureScaleIds,
            grid: mapState.grid,
          },
          widgetLayouts: nextGrid.reduce((acc, gridObj) => {
            const elementId = getId(gridObj);
            const payload = gridObj?.[elementId];
            if (!payload?.map) {
              return acc;
            }
            acc[elementId] = {
              x: Math.round(Number(payload?.map?.x) || 0),
              y: Math.round(Number(payload?.map?.y) || 0),
              w: Math.max(80, Math.round(Number(payload?.map?.w) || 0)),
              h: Math.max(24, Math.round(Number(payload?.map?.h) || 0)),
            };
            return acc;
          }, {}),
        });
      }
    },
    [
      tab,
      boardHistory,
      displayedPins,
      displayedLinks,
      displayedShapes,
      displayedRegions,
      displayedDrawings,
      displayedGroups,
      displayedMeasurements,
      measureScales,
      activeMeasureScaleIds,
      mapState.grid,
    ],
  );
  const persistGrid = useCallback(
    async nextGrid => {
      if (!tab?._id || !Array.isArray(nextGrid)) {
        return;
      }
      await update(`items/${tab._id}`, 'grid', nextGrid);
    },
    [tab?._id],
  );
  useEffect(() => {
    if (!tab?._id || !Array.isArray(tab?.grid) || tab.grid.length === 0) {
      return;
    }
    const containerRect = containerRef.current?.getBoundingClientRect();
    const viewportCenterScreenX = containerRect
      ? containerRect.width / 2
      : (Number.isFinite(boardWidth) && boardWidth > 0 ? boardWidth : 1200) / 2;
    const viewportCenterScreenY = containerRect
      ? containerRect.height / 2
      : 450;
    const viewportCenterWorldX =
      (viewportCenterScreenX - viewport.x) / Math.max(viewport.zoom || 1, 0.1);
    const viewportCenterWorldY =
      (viewportCenterScreenY - viewport.y) / Math.max(viewport.zoom || 1, 0.1);
    let missingLayoutIndex = 0;
    let hasChanges = false;
    const nextGrid = deepCloneArray(tab.grid).map((gridObj, index) => {
      const elementId = getId(gridObj);
      const payload = gridObj?.[elementId];
      if (!elementId || !payload) {
        return gridObj;
      }
      const mapLayout = payload?.map || {};
      if (
        Number.isFinite(mapLayout?.x) &&
        Number.isFinite(mapLayout?.y) &&
        Number.isFinite(mapLayout?.w) &&
        Number.isFinite(mapLayout?.h)
      ) {
        return gridObj;
      }
      hasChanges = true;
      const defaultLayout = getDefaultMapWidgetLayout(payload, index);
      const mergedLayout = {
        ...defaultLayout,
        ...mapLayout,
      };
      const nextW = Math.max(80, Math.round(Number(mergedLayout?.w) || Number(defaultLayout?.w) || 320));
      const nextH = Math.max(24, Math.round(Number(mergedLayout?.h) || Number(defaultLayout?.h) || 120));
      const offsetStepX = 36;
      const offsetStepY = 28;
      const nextX = Number.isFinite(mapLayout?.x)
        ? Math.round(Number(mapLayout.x))
        : Math.round(
            viewportCenterWorldX - nextW / 2 + (missingLayoutIndex % 3) * offsetStepX,
          );
      const nextY = Number.isFinite(mapLayout?.y)
        ? Math.round(Number(mapLayout.y))
        : Math.round(
            viewportCenterWorldY - nextH / 2 + Math.floor(missingLayoutIndex / 3) * offsetStepY,
          );
      missingLayoutIndex += 1;
      return {
        [elementId]: {
          ...payload,
          map: {
            ...mergedLayout,
            x: nextX,
            y: nextY,
            w: nextW,
            h: nextH,
          },
        },
      };
    });
    if (hasChanges) {
      update(`items/${tab._id}`, 'grid', nextGrid);
    }
  }, [tab, boardWidth, viewport.x, viewport.y, viewport.zoom]);
  useEffect(() => {
    setBackgroundUrlInput(mapState?.background?.url || '');
  }, [mapState?.background?.url]);

  useEffect(() => {
    setDisplayedBackground(mapState?.background);
  }, [mapState?.background]);

  useEffect(() => {
    setGridSizeInput(String(mapState?.grid?.size || 50));
  }, [mapState?.grid?.size]);

  useEffect(() => {
    setDisplayedGrid(mapState?.grid);
  }, [mapState?.grid]);

  useEffect(() => {
    setDisplayedPins(mapState?.pins || []);
  }, [mapState?.pins]);

  useEffect(() => {
    setDisplayedLinks(mapState?.links || []);
  }, [mapState?.links]);

  useEffect(() => {
    setDisplayedShapes(mapState?.shapes || []);
  }, [mapState?.shapes]);

  useEffect(() => {
    setDisplayedRegions(mapState?.regions || []);
  }, [mapState?.regions]);

  useEffect(() => {
    setDisplayedDrawings(mapState?.drawings || []);
  }, [mapState?.drawings]);
  useEffect(() => {
    setDisplayedMeasurements(mapState?.measurements || []);
  }, [mapState?.measurements]);
  useEffect(() => {
    const nextScales = mapState?.measureScales || [];
    setMeasureScales(nextScales);
    const rawActiveIds =
      Array.isArray(mapState?.activeMeasureScaleIds) && mapState.activeMeasureScaleIds.length > 0
        ? mapState.activeMeasureScaleIds
        : mapState?.activeMeasureScaleId
          ? [mapState.activeMeasureScaleId]
          : [];
    const normalizedActiveIds = rawActiveIds.filter(scaleId =>
      nextScales.some(scale => scale?.id === scaleId),
    );
    setActiveMeasureScaleIds(normalizedActiveIds);
  }, [mapState?.measureScales, mapState?.activeMeasureScaleId, mapState?.activeMeasureScaleIds]);
  useEffect(() => {
    onMeasureScalesChange?.(measureScales);
  }, [measureScales, onMeasureScalesChange]);
  useEffect(() => {
    onActiveMeasureScaleIdsChange?.(activeMeasureScaleIds);
  }, [activeMeasureScaleIds, onActiveMeasureScaleIdsChange]);
  useEffect(() => {
    if (mapTool === 'measure' && measureMode === 'settings') {
      dispatch(setOpenModales({ id: measureSettingsModalId, open: true }));
    }
  }, [dispatch, mapTool, measureMode, measureSettingsModalId]);
  useEffect(() => {
    setDisplayedGroups(mapState?.groups || []);
  }, [mapState?.groups]);

  useEffect(() => {
    setWidgetLayoutOverrides({});
  }, [tab?.grid]);

  useEffect(() => {
    if (mapTool !== 'region') {
      setRegionDraftPoints([]);
      setRegionDraftHoverPoint(null);
    }
    if (mapTool !== 'draw') {
      setDrawingDraftPoints([]);
    }
    if (mapTool !== 'line') {
      setLineDraft(null);
    }
    if (mapTool !== 'shape') {
      setShapeDraft(null);
    }
  }, [mapTool]);
  useEffect(() => {
    const previousTool = previousMapToolRef.current;
    const didToolChange = previousTool !== mapTool;
    previousMapToolRef.current = mapTool;
    if (!didToolChange || displayedMeasurements.length === 0) {
      return;
    }
    setDisplayedMeasurements([]);
    void persistMapFieldRaw('measurements', []);
  }, [displayedMeasurements.length, mapTool, persistMapFieldRaw]);

  const mapWidgetsMemo = useMemo(
    () =>
      Array.isArray(tab?.grid)
        ? tab.grid.filter(widget => {
            const payload = getData(widget);
            return Boolean(payload?.map);
          })
        : [],
    [tab?.grid],
  );

  useEffect(
    () => () => {
      if (pinClickTimeoutRef.current) {
        window.clearTimeout(pinClickTimeoutRef.current);
      }
      if (shapeClickTimeoutRef.current) {
        window.clearTimeout(shapeClickTimeoutRef.current);
      }
      if (regionClickTimeoutRef.current) {
        window.clearTimeout(regionClickTimeoutRef.current);
      }
    },
    [],
  );

  const mapWidgetsList = mapWidgetsMemo;
  const buildBoardHistorySnapshot = useCallback(() => {
    const latestTab = store.getState()?.items?.[tab?._id] || tab;
    const latestGrid = Array.isArray(latestTab?.grid) ? latestTab.grid : [];
    const widgetLayouts = latestGrid.reduce((acc, gridObj) => {
      const elementId = getId(gridObj);
      const payload = getData(gridObj) || {};
      if (!payload?.map) {
        return acc;
      }
      const override = widgetLayoutOverrides[elementId];
      const mapLayout = override || payload.map || {};
      acc[elementId] = {
        x: Math.round(Number(mapLayout?.x) || 0),
        y: Math.round(Number(mapLayout?.y) || 0),
        w: Math.max(80, Math.round(Number(mapLayout?.w) || 0)),
        h: Math.max(24, Math.round(Number(mapLayout?.h) || 0)),
      };
      return acc;
    }, {});
    return {
      map: {
        pins: deepCloneArray(displayedPins),
        links: deepCloneArray(displayedLinks),
        shapes: deepCloneArray(displayedShapes),
        regions: deepCloneArray(displayedRegions),
        drawings: deepCloneArray(displayedDrawings),
        groups: deepCloneArray(displayedGroups),
        measurements: deepCloneArray(displayedMeasurements),
        measureScales: deepCloneArray(measureScales),
        activeMeasureScaleIds: deepCloneArray(activeMeasureScaleIds),
        grid: cloneHistoryValue(mapState?.grid),
      },
      widgetLayouts,
    };
  }, [
    tab,
    widgetLayoutOverrides,
    displayedPins,
    displayedLinks,
    displayedShapes,
    displayedRegions,
    displayedDrawings,
    displayedGroups,
    displayedMeasurements,
    measureScales,
    activeMeasureScaleIds,
    mapState?.grid,
  ]);

  const persistMapField = useCallback(
    async (field, value, options = {}) => {
      const { skipHistory = false } = options || {};
      await persistMapFieldRaw(field, value);
      if (!skipHistory && !isApplyingHistoryRef.current) {
        const snapshot = buildBoardHistorySnapshot();
        if (field) {
          snapshot.map = {
            ...(snapshot.map || {}),
            [field]: cloneHistoryValue(value),
          };
        }
        boardHistory.record(snapshot);
      }
    },
    [persistMapFieldRaw, boardHistory, buildBoardHistorySnapshot],
  );

  const recordBoardHistorySnapshot = useCallback(() => {
    if (isApplyingHistoryRef.current) {
      return;
    }
    boardHistory.record(buildBoardHistorySnapshot());
  }, [boardHistory, buildBoardHistorySnapshot]);

  const applyBoardHistorySnapshot = useCallback(
    async snapshot => {
      if (!snapshot?.map) {
        return;
      }
      isApplyingHistoryRef.current = true;
      try {
        const nextMap = snapshot.map;
        const nextWidgetLayouts = snapshot.widgetLayouts || {};
        setDisplayedPins(Array.isArray(nextMap.pins) ? nextMap.pins : []);
        setDisplayedLinks(Array.isArray(nextMap.links) ? nextMap.links : []);
        setDisplayedShapes(Array.isArray(nextMap.shapes) ? nextMap.shapes : []);
        setDisplayedRegions(Array.isArray(nextMap.regions) ? nextMap.regions : []);
        setDisplayedDrawings(Array.isArray(nextMap.drawings) ? nextMap.drawings : []);
        setDisplayedGroups(Array.isArray(nextMap.groups) ? nextMap.groups : []);
        setDisplayedMeasurements(
          Array.isArray(nextMap.measurements) ? nextMap.measurements : [],
        );
        setMeasureScales(Array.isArray(nextMap.measureScales) ? nextMap.measureScales : []);
        setActiveMeasureScaleIds(
          Array.isArray(nextMap.activeMeasureScaleIds)
            ? nextMap.activeMeasureScaleIds
            : [],
        );
        setWidgetLayoutOverrides(nextWidgetLayouts);

        await Promise.all([
          persistMapFieldRaw('pins', Array.isArray(nextMap.pins) ? nextMap.pins : []),
          persistMapFieldRaw('links', Array.isArray(nextMap.links) ? nextMap.links : []),
          persistMapFieldRaw('shapes', Array.isArray(nextMap.shapes) ? nextMap.shapes : []),
          persistMapFieldRaw('regions', Array.isArray(nextMap.regions) ? nextMap.regions : []),
          persistMapFieldRaw(
            'drawings',
            Array.isArray(nextMap.drawings) ? nextMap.drawings : [],
          ),
          persistMapFieldRaw('groups', Array.isArray(nextMap.groups) ? nextMap.groups : []),
          persistMapFieldRaw(
            'measurements',
            Array.isArray(nextMap.measurements) ? nextMap.measurements : [],
          ),
          persistMapFieldRaw(
            'measureScales',
            Array.isArray(nextMap.measureScales) ? nextMap.measureScales : [],
          ),
          persistMapFieldRaw(
            'activeMeasureScaleIds',
            Array.isArray(nextMap.activeMeasureScaleIds)
              ? nextMap.activeMeasureScaleIds
              : [],
          ),
          persistMapFieldRaw(
            'grid',
            nextMap.grid && typeof nextMap.grid === 'object'
              ? nextMap.grid
              : mapState.grid,
          ),
          ...Object.keys(nextWidgetLayouts).map(widgetId =>
            persistMapWidgetLayout(widgetId, nextWidgetLayouts[widgetId], {
              skipHistory: true,
            }),
          ),
        ]);
      } finally {
        isApplyingHistoryRef.current = false;
      }
    },
    [mapState.grid, persistMapFieldRaw, persistMapWidgetLayout],
  );

  const { reset: resetBoardHistory, undo: undoBoardHistory, redo: redoBoardHistory } = boardHistory;

  useEffect(() => {
    if (!tab?._id) {
      return;
    }
    const latestTab = store.getState()?.items?.[tab._id] || tab;
    const latestGrid = Array.isArray(latestTab?.grid) ? latestTab.grid : [];
    const widgetLayouts = latestGrid.reduce((acc, gridObj) => {
      const elementId = getId(gridObj);
      const payload = getData(gridObj) || {};
      if (!payload?.map) {
        return acc;
      }
      acc[elementId] = {
        x: Math.round(Number(payload?.map?.x) || 0),
        y: Math.round(Number(payload?.map?.y) || 0),
        w: Math.max(80, Math.round(Number(payload?.map?.w) || 0)),
        h: Math.max(24, Math.round(Number(payload?.map?.h) || 0)),
      };
      return acc;
    }, {});
    resetBoardHistory({
      map: {
        pins: deepCloneArray(mapState?.pins || []),
        links: deepCloneArray(mapState?.links || []),
        shapes: deepCloneArray(mapState?.shapes || []),
        regions: deepCloneArray(mapState?.regions || []),
        drawings: deepCloneArray(mapState?.drawings || []),
        groups: deepCloneArray(mapState?.groups || []),
        measurements: deepCloneArray(mapState?.measurements || []),
        measureScales: deepCloneArray(mapState?.measureScales || []),
        activeMeasureScaleIds: deepCloneArray(mapState?.activeMeasureScaleIds || []),
        grid: cloneHistoryValue(mapState?.grid),
      },
      widgetLayouts,
    });
  }, [
    mapState?.activeMeasureScaleIds,
    mapState?.drawings,
    mapState?.grid,
    mapState?.groups,
    mapState?.links,
    mapState?.measureScales,
    mapState?.measurements,
    mapState?.pins,
    mapState?.regions,
    mapState?.shapes,
    resetBoardHistory,
    tab,
  ]);

  useEffect(() => {
    const handleHistoryKeys = event => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      const isUndo = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey;
      const isRedo = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && event.shiftKey;
      if (!isUndo && !isRedo) {
        return;
      }
      event.preventDefault();
      const snapshot = isUndo ? undoBoardHistory() : redoBoardHistory();
      if (snapshot) {
        applyBoardHistorySnapshot(snapshot);
      }
    };
    window.addEventListener('keydown', handleHistoryKeys);
    return () => window.removeEventListener('keydown', handleHistoryKeys);
  }, [undoBoardHistory, redoBoardHistory, applyBoardHistorySnapshot]);
  const activeGroup = useMemo(
    () => displayedGroups.find(group => group?.id === activeGroupId) || null,
    [displayedGroups, activeGroupId],
  );
  const activeGroupChildIds = useMemo(
    () => new Set(Array.isArray(activeGroup?.childIds) ? activeGroup.childIds : []),
    [activeGroup],
  );
  const visibleMapWidgetsList = useMemo(
    () =>
      activeGroup
        ? mapWidgetsList.filter(widget => activeGroupChildIds.has(toMapWidgetNodeId(getId(widget))))
        : mapWidgetsList,
    [activeGroup, activeGroupChildIds, mapWidgetsList],
  );
  const visiblePins = useMemo(
    () =>
      activeGroup
        ? displayedPins.filter(pin => activeGroupChildIds.has(toMapPinNodeId(pin.id)))
        : displayedPins,
    [activeGroup, activeGroupChildIds, displayedPins],
  );
  const visibleShapes = useMemo(
    () =>
      activeGroup
        ? displayedShapes.filter(shape => activeGroupChildIds.has(toMapShapeNodeId(shape.id)))
        : displayedShapes,
    [activeGroup, activeGroupChildIds, displayedShapes],
  );
  const visibleRegions = useMemo(
    () =>
      activeGroup
        ? displayedRegions.filter(region => activeGroupChildIds.has(toMapRegionNodeId(region.id)))
        : displayedRegions,
    [activeGroup, activeGroupChildIds, displayedRegions],
  );
  const visibleDrawings = useMemo(
    () =>
      activeGroup
        ? displayedDrawings.filter(drawing => activeGroupChildIds.has(toMapDrawingNodeId(drawing.id)))
        : displayedDrawings,
    [activeGroup, activeGroupChildIds, displayedDrawings],
  );
  const visibleGroups = useMemo(
    () => (activeGroup ? [] : displayedGroups),
    [activeGroup, displayedGroups],
  );
  const getMapGroupChildRect = useCallback(
    nodeId => {
      const parsed = parseMapNodeId(nodeId);
      if (!parsed?.type || !parsed?.id) {
        return null;
      }
      if (parsed.type === 'widget') {
        const widget = mapWidgetsList.find(entry => getId(entry) === parsed.id);
        const payload = widget ? getData(widget) || {} : {};
        const layout = widgetLayoutOverrides[parsed.id] || payload?.map || {};
        return {
          x: Number(layout?.x) || 0,
          y: Number(layout?.y) || 0,
          w: Math.max(80, Number(layout?.w) || 0),
          h: Math.max(24, Number(layout?.h) || 0),
        };
      }
      if (parsed.type === 'pin') {
        const pin = displayedPins.find(entry => entry?.id === parsed.id);
        if (!pin) {
          return null;
        }
        const sizeById = { sm: 18, md: 30, lg: 42 };
        const size = sizeById[pin?.size] || sizeById.md;
        const x = Number(pin?.x) || 0;
        const y = Number(pin?.y) || 0;
        return {
          x: x - size / 2,
          y: y - size,
          w: size,
          h: size,
        };
      }
      if (parsed.type === 'shape') {
        const shape = displayedShapes.find(entry => entry?.id === parsed.id);
        if (!shape) {
          return null;
        }
        if (shape?.type === 'path' && Array.isArray(shape?.points) && shape.points.length >= 2) {
          const xs = shape.points.map(point => Number(point?.x) || 0);
          const ys = shape.points.map(point => Number(point?.y) || 0);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);
          return {
            x: minX,
            y: minY,
            w: Math.max(1, maxX - minX),
            h: Math.max(1, maxY - minY),
          };
        }
        return {
          x: Number(shape?.x) || 0,
          y: Number(shape?.y) || 0,
          w: Math.max(8, Number(shape?.w) || 0),
          h: Math.max(8, Number(shape?.h) || 0),
        };
      }
      if (parsed.type === 'region') {
        const region = displayedRegions.find(entry => entry?.id === parsed.id);
        const points = Array.isArray(region?.points) ? region.points : [];
        if (!points.length) {
          return null;
        }
        const xs = points.map(point => Number(point?.x) || 0);
        const ys = points.map(point => Number(point?.y) || 0);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return {
          x: minX,
          y: minY,
          w: Math.max(1, maxX - minX),
          h: Math.max(1, maxY - minY),
        };
      }
      if (parsed.type === 'drawing') {
        const drawing = displayedDrawings.find(entry => entry?.id === parsed.id);
        const points = Array.isArray(drawing?.points) ? drawing.points : [];
        if (points.length < 2) {
          return null;
        }
        const xs = points.map(point => Number(point?.x) || 0);
        const ys = points.map(point => Number(point?.y) || 0);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return {
          x: minX,
          y: minY,
          w: Math.max(1, maxX - minX),
          h: Math.max(1, maxY - minY),
        };
      }
      return null;
    },
    [
      displayedDrawings,
      displayedPins,
      displayedRegions,
      displayedShapes,
      mapWidgetsList,
      widgetLayoutOverrides,
    ],
  );
  const attachMapNodeToActiveGroup = useCallback(
    async ({ nodeId, rect, persist = true }) => {
      if (!activeGroupId || !nodeId) {
        return displayedGroups;
      }
      const nextGroups = attachNodeToGroups({
        groups: displayedGroups,
        activeGroupId,
        nodeId,
        rect,
      });
      setDisplayedGroups(nextGroups);
      if (persist) {
        await persistMapField('groups', nextGroups);
      }
      return nextGroups;
    },
    [activeGroupId, displayedGroups, persistMapField],
  );

  useEffect(() => {
    if (!displayedGroups.length) {
      return;
    }
    if (
      pinDragStateRef.current ||
      shapeDragStateRef.current ||
      shapeResizeStateRef.current ||
      widgetDragStateRef.current ||
      widgetResizeStateRef.current ||
      groupDragStateRef.current ||
      regionDragStateRef.current ||
      regionPointDragStateRef.current ||
      drawingDragStateRef.current ||
      drawingStateRef.current ||
      shapeDraftRef.current ||
      regionDrawStateRef.current
    ) {
      return;
    }

    const nextGroups = displayedGroups.map(group => {
      const childIds = Array.isArray(group?.childIds) ? group.childIds : [];
      const childRects = childIds.map(getMapGroupChildRect).filter(Boolean);
      if (!childRects.length) {
        return group;
      }
      const minX = Math.min(...childRects.map(rect => rect.x));
      const minY = Math.min(...childRects.map(rect => rect.y));
      const maxX = Math.max(...childRects.map(rect => rect.x + rect.w));
      const maxY = Math.max(...childRects.map(rect => rect.y + rect.h));
      const nextX = Math.round(minX);
      const nextY = Math.round(minY);
      const nextW = Math.max(1, Math.round(maxX - minX));
      const nextH = Math.max(1, Math.round(maxY - minY));
      if (
        nextX === (Number(group?.x) || 0) &&
        nextY === (Number(group?.y) || 0) &&
        nextW === Math.max(1, Math.round(Number(group?.w) || 0)) &&
        nextH === Math.max(1, Math.round(Number(group?.h) || 0))
      ) {
        return group;
      }
      return {
        ...group,
        x: nextX,
        y: nextY,
        w: nextW,
        h: nextH,
      };
    });

    const hasChanges = nextGroups.some((group, index) => group !== displayedGroups[index]);
    if (!hasChanges) {
      return;
    }
    setDisplayedGroups(nextGroups);
    persistMapField('groups', nextGroups);
  }, [
    displayedGroups,
    displayedDrawings,
    displayedPins,
    displayedRegions,
    displayedShapes,
    getMapGroupChildRect,
    persistMapField,
    widgetLayoutOverrides,
  ]);

  const mapNodeRectById = useMemo(() => {
    const nextMap = new Map();
    const pinSizeValue = { sm: 18, md: 30, lg: 42 };

    visiblePins.forEach(pin => {
      const size = pinSizeValue[pin?.size] || pinSizeValue.md;
      const x = Number(pin?.x) || 0;
      const y = Number(pin?.y) || 0;
      nextMap.set(toMapPinNodeId(pin.id), {
        cx: x,
        cy: y - size / 2,
        w: size,
        h: size,
      });
    });

    visibleShapes.forEach(shape => {
      if (!shape?.id) {
        return;
      }
      if (shape?.type === 'path' && Array.isArray(shape?.points) && shape.points.length >= 2) {
        const xs = shape.points.map(point => Number(point?.x) || 0);
        const ys = shape.points.map(point => Number(point?.y) || 0);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        nextMap.set(toMapShapeNodeId(shape.id), {
          cx: (minX + maxX) / 2,
          cy: (minY + maxY) / 2,
          w: Math.max(8, maxX - minX),
          h: Math.max(8, maxY - minY),
        });
        return;
      }
      const x = Number(shape?.x) || 0;
      const y = Number(shape?.y) || 0;
      const w = Math.max(8, Number(shape?.w) || 0);
      const h = Math.max(8, Number(shape?.h) || 0);
      nextMap.set(toMapShapeNodeId(shape.id), {
        cx: x + w / 2,
        cy: y + h / 2,
        w,
        h,
      });
    });

    visibleRegions.forEach(region => {
      const points = Array.isArray(region?.points) ? region.points : [];
      if (!region?.id || !points.length) {
        return;
      }
      const xs = points.map(point => Number(point?.x) || 0);
      const ys = points.map(point => Number(point?.y) || 0);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      nextMap.set(toMapRegionNodeId(region.id), {
        cx: (minX + maxX) / 2,
        cy: (minY + maxY) / 2,
        w: Math.max(8, maxX - minX),
        h: Math.max(8, maxY - minY),
      });
    });

    visibleDrawings.forEach(drawing => {
      const points = Array.isArray(drawing?.points) ? drawing.points : [];
      if (!drawing?.id || points.length < 2) {
        return;
      }
      const xs = points.map(point => Number(point?.x) || 0);
      const ys = points.map(point => Number(point?.y) || 0);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      nextMap.set(toMapDrawingNodeId(drawing.id), {
        cx: (minX + maxX) / 2,
        cy: (minY + maxY) / 2,
        w: Math.max(8, maxX - minX),
        h: Math.max(8, maxY - minY),
      });
    });

    visibleMapWidgetsList.forEach(widget => {
      const widgetId = getId(widget);
      const payload = getData(widget) || {};
      const layout = widgetLayoutOverrides[widgetId] || payload?.map || {};
      const x = Number(layout?.x) || 0;
      const y = Number(layout?.y) || 0;
      const w = Math.max(80, Number(layout?.w) || 0);
      const h = Math.max(24, Number(layout?.h) || 0);
      nextMap.set(toMapWidgetNodeId(widgetId), {
        cx: x + w / 2,
        cy: y + h / 2,
        w,
        h,
      });
    });

    displayedGroups.forEach(group => {
      if (!group?.id) {
        return;
      }
      const x = Number(group?.x) || 0;
      const y = Number(group?.y) || 0;
      const w = Math.max(24, Number(group?.w) || 0);
      const h = Math.max(24, Number(group?.h) || 0);
      nextMap.set(toMapGroupNodeId(group.id), {
        cx: x + w / 2,
        cy: y + h / 2,
        w,
        h,
      });
    });

    return nextMap;
  }, [
    displayedGroups,
    visibleDrawings,
    visiblePins,
    visibleRegions,
    visibleShapes,
    visibleMapWidgetsList,
    widgetLayoutOverrides,
  ]);

  const mapNodeMetaById = useMemo(() => {
    const nextMap = new Map();
    visiblePins.forEach(pin => {
      if (pin?.id) {
        nextMap.set(toMapPinNodeId(pin.id), {
          type: 'pin',
          color: pin?.color || 'secondary',
        });
      }
    });
    visibleShapes.forEach(shape => {
      if (shape?.id) {
        nextMap.set(toMapShapeNodeId(shape.id), {
          type: 'shape',
          shapeType: shape?.type || 'rect',
          color: shape?.stroke || 'secondary',
        });
      }
    });
    visibleRegions.forEach(region => {
      if (region?.id) {
        nextMap.set(toMapRegionNodeId(region.id), {
          type: 'region',
          color: region?.stroke || region?.fill || 'secondary',
        });
      }
    });
    visibleDrawings.forEach(drawing => {
      if (drawing?.id) {
        nextMap.set(toMapDrawingNodeId(drawing.id), {
          type: 'drawing',
          color: drawing?.stroke || 'secondary',
        });
      }
    });
    visibleMapWidgetsList.forEach(widget => {
      const widgetId = getId(widget);
      if (widgetId) {
        nextMap.set(toMapWidgetNodeId(widgetId), {
          type: 'widget',
          color: 'contrast',
        });
      }
    });
    visibleGroups.forEach(group => {
      if (group?.id) {
        nextMap.set(toMapGroupNodeId(group.id), {
          type: 'group',
          color: group?.style?.stroke || 'secondary',
        });
      }
    });
    return nextMap;
  }, [
    visibleDrawings,
    visibleGroups,
    visibleMapWidgetsList,
    visiblePins,
    visibleRegions,
    visibleShapes,
  ]);

  const mapRenderMetrics = useMemo(() => {
    const baseWidth =
      Number.isFinite(boardWidth) && boardWidth > 0
        ? Math.max(boardWidth, 1200)
        : 1200;
    const backgroundWidth =
      Number(mapState?.background?.width) > 0 ? Number(mapState.background.width) : 0;
    const backgroundHeight =
      Number(mapState?.background?.height) > 0 ? Number(mapState.background.height) : 0;
    const baseHeight = Math.max(Math.round(baseWidth * 0.66), backgroundHeight || 900);
    let minX = 0;
    let minY = 0;
    let maxX = Math.max(baseWidth, backgroundWidth);
    let maxY = Math.max(baseHeight, backgroundHeight);

    displayedPins.forEach(pin => {
      const pinX = Number(pin?.x) || 0;
      const pinY = Number(pin?.y) || 0;
      minX = Math.min(minX, pinX);
      minY = Math.min(minY, pinY);
      maxX = Math.max(maxX, pinX);
      maxY = Math.max(maxY, pinY);
    });

    displayedShapes.forEach(shape => {
      minX = Math.min(minX, Number(shape?.x) || 0);
      minY = Math.min(minY, Number(shape?.y) || 0);
      maxX = Math.max(maxX, (Number(shape?.x) || 0) + (Number(shape?.w) || 0));
      maxY = Math.max(maxY, (Number(shape?.y) || 0) + (Number(shape?.h) || 0));
    });

    displayedRegions.forEach(region => {
      const points = Array.isArray(region?.points) ? region.points : [];
      points.forEach(point => {
        const pointX = Number(point?.x) || 0;
        const pointY = Number(point?.y) || 0;
        minX = Math.min(minX, pointX);
        minY = Math.min(minY, pointY);
        maxX = Math.max(maxX, pointX);
        maxY = Math.max(maxY, pointY);
      });
    });

    displayedDrawings.forEach(drawing => {
      const points = Array.isArray(drawing?.points) ? drawing.points : [];
      points.forEach(point => {
        const pointX = Number(point?.x) || 0;
        const pointY = Number(point?.y) || 0;
        minX = Math.min(minX, pointX);
        minY = Math.min(minY, pointY);
        maxX = Math.max(maxX, pointX);
        maxY = Math.max(maxY, pointY);
      });
    });

    mapWidgetsList.forEach(widget => {
      const elementId = getId(widget);
      const payload = getData(widget) || {};
      const layout = widgetLayoutOverrides[elementId] || payload?.map || {};
      const x = Number(layout?.x) || 0;
      const y = Number(layout?.y) || 0;
      const w = Math.max(80, Number(layout?.w) || 0);
      const h = Math.max(24, Number(layout?.h) || 0);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });

    const margin = 80;
    const offsetX = minX < margin ? margin - minX : 0;
    const offsetY = minY < margin ? margin - minY : 0;

    return {
      baseWidth,
      baseHeight,
      offsetX,
      offsetY,
      canvasWidth: Math.max(baseWidth, maxX + offsetX + margin),
      canvasHeight: Math.max(baseHeight, maxY + offsetY + margin),
    };
  }, [
    boardWidth,
    displayedPins,
    displayedShapes,
    displayedRegions,
    displayedDrawings,
    mapState?.background?.height,
    mapState?.background?.width,
    mapWidgetsList,
    widgetLayoutOverrides,
  ]);

  const activeMeasureScales = useMemo(
    () =>
      measureScales.filter(scale => activeMeasureScaleIds.includes(scale?.id)),
    [activeMeasureScaleIds, measureScales],
  );

  const handleMeasureDraftComplete = useCallback(
    async draft => {
      if (!draft) {
        return;
      }
      if (draft.settingsMode) {
        setPendingMeasureReference(draft);
        setMeasureScaleValueInput('');
        setMeasureScaleLabelInput('');
        setMeasureScaleError('');
        dispatch(setOpenModales({ id: measureSettingsModalId, open: true }));
        return;
      }
      const draftPoints = Array.isArray(draft?.points)
        ? draft.points
            .filter(
              point =>
                Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)),
            )
            .map(point => ({
              x: Number(point.x),
              y: Number(point.y),
            }))
        : [];
      const normalizedDraftPoints =
        draftPoints.length >= 2
          ? draftPoints
          : [
              { x: Number(draft?.x1 || 0), y: Number(draft?.y1 || 0) },
              { x: Number(draft?.x2 || 0), y: Number(draft?.y2 || 0) },
            ];
      const extensionTarget = findMeasureExtensionTarget({
        measurements: displayedMeasurements,
        startPoint: normalizedDraftPoints[0],
        zoom: viewport.zoom,
      });
      if (extensionTarget?.measurementId) {
        const nextMeasurements = displayedMeasurements.map(measurement => {
          if (measurement?.id !== extensionTarget.measurementId) {
            return measurement;
          }
          const existingPoints = getMeasurementPoints(measurement);
          if (existingPoints.length < 2 || normalizedDraftPoints.length < 2) {
            return measurement;
          }

          let mergedPoints = existingPoints;
          if (extensionTarget.side === 'start') {
            const reversedDraftPoints = [...normalizedDraftPoints].reverse();
            reversedDraftPoints[reversedDraftPoints.length - 1] = existingPoints[0];
            mergedPoints = [...reversedDraftPoints, ...existingPoints.slice(1)];
          } else {
            const forwardDraftPoints = [...normalizedDraftPoints];
            forwardDraftPoints[0] = existingPoints[existingPoints.length - 1];
            mergedPoints = [...existingPoints.slice(0, -1), ...forwardDraftPoints];
          }

          return {
            ...measurement,
            x1: Number(mergedPoints[0]?.x || 0),
            y1: Number(mergedPoints[0]?.y || 0),
            x2: Number(mergedPoints[mergedPoints.length - 1]?.x || 0),
            y2: Number(mergedPoints[mergedPoints.length - 1]?.y || 0),
            points: mergedPoints,
            length: computeMeasurementLength(mergedPoints),
          };
        });
        setDisplayedMeasurements(nextMeasurements);
        await persistMapField('measurements', nextMeasurements);
        return;
      }
      const nextMeasurement = {
        id:
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `measure_${Date.now()}`,
        x1: Number(normalizedDraftPoints[0]?.x || 0),
        y1: Number(normalizedDraftPoints[0]?.y || 0),
        x2: Number(normalizedDraftPoints[normalizedDraftPoints.length - 1]?.x || 0),
        y2: Number(normalizedDraftPoints[normalizedDraftPoints.length - 1]?.y || 0),
        length: computeMeasurementLength(normalizedDraftPoints),
        points: normalizedDraftPoints,
      };
      // Keep only one active measurement on screen at a time.
      const nextMeasurements = [nextMeasurement];
      setDisplayedMeasurements(nextMeasurements);
      await persistMapField('measurements', nextMeasurements);
    },
    [dispatch, displayedMeasurements, measureSettingsModalId, persistMapField, viewport.zoom],
  );

  const handleSaveMeasureScale = useCallback(async () => {
    const value = Number(String(measureScaleValueInput || '').replace(',', '.'));
    const unit = String(measureScaleUnitInput || '').trim();
    const referencePx = Number(pendingMeasureReference?.length);
    if (!Number.isFinite(referencePx) || referencePx <= 0) {
      setMeasureScaleError('Trace de reference invalide.');
      return;
    }
    if (!Number.isFinite(value) || value <= 0) {
      setMeasureScaleError('Entre une valeur numerique > 0.');
      return;
    }
    if (!unit) {
      setMeasureScaleError('Entre une unite.');
      return;
    }
    const nextScale = {
      id:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `scale_${Date.now()}`,
      label: String(measureScaleLabelInput || '').trim(),
      value,
      unit,
      referencePx,
    };
    const nextScales = [...measureScales, nextScale];
    const nextActiveIds = [...new Set([...activeMeasureScaleIds, nextScale.id])];
    setMeasureScales(nextScales);
    setActiveMeasureScaleIds(nextActiveIds);
    setPendingMeasureReference(null);
    setMeasureScaleError('');
    await Promise.all([
      persistMapField('measureScales', nextScales),
      persistMapField('activeMeasureScaleIds', nextActiveIds),
      persistMapField('activeMeasureScaleId', nextActiveIds[0] || null),
    ]);
  }, [
    activeMeasureScaleIds,
    measureScaleLabelInput,
    measureScaleUnitInput,
    measureScaleValueInput,
    measureScales,
    pendingMeasureReference?.length,
    persistMapField,
  ]);

  const handleToggleMeasureScale = useCallback(
    async scaleId => {
      if (!scaleId) {
        return;
      }
      const nextActiveIds = activeMeasureScaleIds.includes(scaleId)
        ? activeMeasureScaleIds.filter(id => id !== scaleId)
        : [...activeMeasureScaleIds, scaleId];
      setActiveMeasureScaleIds(nextActiveIds);
      await Promise.all([
        persistMapField('activeMeasureScaleIds', nextActiveIds),
        persistMapField('activeMeasureScaleId', nextActiveIds[0] || null),
      ]);
    },
    [activeMeasureScaleIds, persistMapField],
  );

  const handleDeleteMeasureScale = useCallback(
    async scaleId => {
      const nextScales = measureScales.filter(scale => scale?.id !== scaleId);
      const nextActiveIds = activeMeasureScaleIds.filter(id => id !== scaleId);
      setMeasureScales(nextScales);
      setActiveMeasureScaleIds(nextActiveIds);
      await Promise.all([
        persistMapField('measureScales', nextScales),
        persistMapField('activeMeasureScaleIds', nextActiveIds),
        persistMapField('activeMeasureScaleId', nextActiveIds[0] || null),
      ]);
    },
    [activeMeasureScaleIds, measureScales, persistMapField],
  );

  const handleMultiplyMeasureScale = useCallback(
    async (scaleId, multiplier, nextUnitValue = null) => {
      const baseScale = measureScales.find(scale => scale?.id === scaleId);
      const numericMultiplier = Number(multiplier);
      if (!baseScale || !Number.isFinite(numericMultiplier) || numericMultiplier <= 0) {
        return;
      }
      const baseValue = Number(baseScale?.value);
      if (!Number.isFinite(baseValue) || baseValue <= 0) {
        return;
      }
      const nextScale = {
        id:
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `scale_${Date.now()}`,
        label: '',
        value: baseValue * numericMultiplier,
        unit:
          typeof nextUnitValue === 'string' && nextUnitValue.trim()
            ? nextUnitValue.trim()
            : String(baseScale?.unit || '').trim(),
        referencePx: Number(baseScale?.referencePx || 0),
      };
      const nextScales = [...measureScales, nextScale];
      const nextActiveIds = [...new Set([...activeMeasureScaleIds, nextScale.id])];
      setMeasureScales(nextScales);
      setActiveMeasureScaleIds(nextActiveIds);
      await Promise.all([
        persistMapField('measureScales', nextScales),
        persistMapField('activeMeasureScaleIds', nextActiveIds),
        persistMapField('activeMeasureScaleId', nextActiveIds[0] || null),
      ]);
    },
    [activeMeasureScaleIds, measureScales, persistMapField],
  );
  const handleUpdateMeasureScale = useCallback(
    async (scaleId, updates) => {
      if (!scaleId || !updates) {
        return;
      }
      const rawValue = Number(String(updates?.value ?? '').replace(',', '.'));
      const rawReferencePx = Number(String(updates?.referencePx ?? '').replace(',', '.'));
      const rawUnit = String(updates?.unit || '').trim();
      if (!Number.isFinite(rawValue) || rawValue <= 0) {
        return;
      }
      if (!Number.isFinite(rawReferencePx) || rawReferencePx <= 0) {
        return;
      }
      if (!rawUnit) {
        return;
      }
      const nextScales = measureScales.map(scale =>
        scale?.id === scaleId
          ? {
              ...scale,
              value: rawValue,
              unit: rawUnit,
              referencePx: rawReferencePx,
            }
          : scale,
      );
      setMeasureScales(nextScales);
      await persistMapField('measureScales', nextScales);
    },
    [measureScales, persistMapField],
  );
  const handleCloseMeasureSettingsAndTakeMeasure = useCallback(() => {
    dispatch(setOpenModales({ id: measureSettingsModalId, open: false }));
    setPendingMeasureReference(null);
    setMeasureScaleError('');
    onMeasureModeChange?.('measure');
  }, [dispatch, measureSettingsModalId, onMeasureModeChange]);

  const handleDeleteMeasurement = useCallback(
    async measurementId => {
      const nextMeasurements = displayedMeasurements.filter(
        measurement => measurement?.id !== measurementId,
      );
      setDisplayedMeasurements(nextMeasurements);
      await persistMapField('measurements', nextMeasurements);
    },
    [displayedMeasurements, persistMapField],
  );

  const {
    handleCanvasPointerDown,
    handlePinPointerDown,
    handleShapePointerDown,
    handleShapeResizePointerDown,
    handleRegionPointerDown,
    handleRegionPointPointerDown,
    handleDrawingPointerDown,
    handleWidgetDragStart,
    handleWidgetResizeStart,
    handleGroupPointerDown,
  } = useBoardMapInteractions({
    editable: layoutEditable,
    containerRef,
    isAnyModalOpen,
    mapTool,
    measureMode,
    mapShapeType,
    effectiveInteractionMode,
    viewport,
    setViewport,
    displayedPins,
    setDisplayedPins,
    displayedLinks,
    setDisplayedLinks,
    displayedShapes,
    setDisplayedShapes,
    displayedRegions,
    setDisplayedRegions,
    displayedDrawings,
    setDisplayedDrawings,
    displayedGroups,
    setDisplayedGroups,
    mapWidgets: visibleMapWidgetsList,
    widgetLayoutOverrides,
    setWidgetLayoutOverrides,
    mapState,
    persistMapField,
    persistMapWidgetLayout,
    clearSelectionBox,
    selectionBox,
    setSelectionBox,
    clearSelection,
    setSelectionState,
    selectPinOnly,
    selectLinkOnly,
    selectShapeOnly,
    selectRegionOnly,
    selectDrawingOnly,
    selectWidgetOnly,
    selectGroupOnly,
    pinDragStateRef,
    lineDraftRef,
    linkCurveDragStateRef,
    shapeDragStateRef,
    shapeResizeStateRef,
    drawingStateRef,
    shapeDraftRef,
    widgetDragStateRef,
    widgetResizeStateRef,
    groupDragStateRef,
    regionDragStateRef,
    regionPointDragStateRef,
    drawingDragStateRef,
    selectionDragStateRef,
    regionDrawStateRef,
    didPanRef,
    didPinDragRef,
    didShapeDragRef,
    didWidgetDragRef,
    didSelectionDragRef,
    didRegionDrawRef,
    didRegionPointDragRef,
    didDrawingRef,
    panStateRef,
    selectedPinIds,
    selectedShapeIds,
    selectedRegionIds,
    selectedDrawingIds,
    selectedWidgetIds,
    selectedGroupIds,
    mapNodeRectById,
    attachMapNodeToActiveGroup,
    setShapeDraft,
    setRegionDraftPoints,
    setRegionDraftHoverPoint,
    setDrawingDraftPoints,
    setLineDraft,
    getSnappedRegionPoint,
    isMapElementTarget,
    getMapTargetNodeId,
    onMeasureDraftComplete: handleMeasureDraftComplete,
    renderMetrics: mapRenderMetrics,
  });
  const canvasWidth = mapRenderMetrics.canvasWidth;
  const canvasHeight = mapRenderMetrics.canvasHeight;
  const gridViewportWidth =
    containerRef.current?.clientWidth ||
    (Number.isFinite(boardWidth) && boardWidth > 0 ? boardWidth : canvasWidth);
  const gridViewportHeight =
    containerRef.current?.clientHeight || mapRenderMetrics.baseHeight || canvasHeight;
  const backShapes = useMemo(
    () => visibleShapes.filter(shape => (shape?.layer || 'back') === 'back'),
    [visibleShapes],
  );
  const frontShapes = useMemo(
    () => visibleShapes.filter(shape => (shape?.layer || 'back') === 'front'),
    [visibleShapes],
  );
  const { isDropActive } = useBoardMapExternalDrop({
    editable: layoutEditable,
    isAnyModalOpen,
    tab,
    itemId,
    boardWidth,
    containerRef,
    viewport,
    renderMetrics: mapRenderMetrics,
    persistGrid,
    activeGroupId,
    displayedGroups,
    setDisplayedGroups,
    persistMapField,
  });
  const selectedPin = useMemo(
    () =>
      Array.isArray(selectedPinIds) && selectedPinIds.length === 1
        ? displayedPins.find(pin => pin.id === selectedPinIds[0]) || null
        : null,
    [displayedPins, selectedPinIds],
  );
  const selectedPins = useMemo(
    () =>
      displayedPins.filter(pin =>
        Array.isArray(selectedPinIds) && selectedPinIds.includes(pin.id),
      ),
    [displayedPins, selectedPinIds],
  );
  const selectedRegion = useMemo(
    () => displayedRegions.find(region => region.id === selectedRegionId) || null,
    [displayedRegions, selectedRegionId],
  );
  const selectedRegions = useMemo(
    () =>
      displayedRegions.filter(region =>
        Array.isArray(selectedRegionIds) && selectedRegionIds.includes(region.id),
      ),
    [displayedRegions, selectedRegionIds],
  );
  const selectedShapes = useMemo(
    () =>
      displayedShapes.filter(shape =>
        Array.isArray(selectedShapeIds) && selectedShapeIds.includes(shape.id),
      ),
    [displayedShapes, selectedShapeIds],
  );
  const selectedShape = useMemo(
    () =>
      Array.isArray(selectedShapeIds) && selectedShapeIds.length === 1
        ? displayedShapes.find(shape => shape.id === selectedShapeIds[0]) || null
        : null,
    [displayedShapes, selectedShapeIds],
  );
  const selectedDrawings = useMemo(
    () =>
      displayedDrawings.filter(drawing =>
        Array.isArray(selectedDrawingIds) && selectedDrawingIds.includes(drawing.id),
      ),
    [displayedDrawings, selectedDrawingIds],
  );
  const selectedLinks = useMemo(
    () =>
      displayedLinks.filter(link =>
        Array.isArray(selectedLinkIds) && selectedLinkIds.includes(link.id),
      ),
    [displayedLinks, selectedLinkIds],
  );
  const selectedLink = useMemo(
    () =>
      Array.isArray(selectedLinkIds) && selectedLinkIds.length === 1
        ? displayedLinks.find(link => link.id === selectedLinkIds[0]) || null
        : null,
    [displayedLinks, selectedLinkIds],
  );
  const selectedLinkGeometry = useMemo(() => {
    if (!selectedLink?.id) {
      return null;
    }
    return getLinkGeometry(selectedLink, mapNodeRectById, mapRenderMetrics);
  }, [selectedLink, mapNodeRectById, mapRenderMetrics]);
  const selectedLinkTarget = selectedPin
    ? { type: 'pin', entity: selectedPin }
    : selectedShape
      ? { type: 'shape', entity: selectedShape }
    : selectedRegion
      ? { type: 'region', entity: selectedRegion }
      : null;
  const linkActionMode = useMemo(() => {
    const linkableSelection = [...selectedPins, ...selectedShapes, ...selectedRegions];
    if (!linkableSelection.length) {
      return 'none';
    }
    if (linkableSelection.length === 1) {
      return linkableSelection[0]?.linkedItemId ? 'unlink' : 'link';
    }
    return linkableSelection.some(entity => Boolean(entity?.linkedItemId))
      ? 'unlink'
      : 'none';
  }, [selectedPins, selectedRegions, selectedShapes]);
  const linkedItemLabelsByPinId = useMemo(
    () =>
      displayedPins.reduce((acc, pin) => {
        const linkedItemId = pin?.linkedItemId;
        const linkedItemLabel = linkedItemId ? items?.[linkedItemId]?.label : null;
        if (linkedItemId && linkedItemLabel) {
          acc[pin.id] = linkedItemLabel;
        }
        return acc;
      }, {}),
    [displayedPins, items],
  );
  const autoItemLinks = useMemo(() => {
    if (!Array.isArray(visibleMapWidgetsList) || visibleMapWidgetsList.length < 2) {
      return [];
    }

    const widgetItemIds = new Set(
      visibleMapWidgetsList
        .filter(widget => getData(widget)?.type === 'link')
        .map(widget => getId(widget))
        .filter(Boolean),
    );

    if (widgetItemIds.size < 2) {
      return [];
    }

    const manualPairs = new Set(
      displayedLinks
        .filter(link => link && link.from && link.to)
        .map(link => getPairKey(link.from, link.to)),
    );

    const seenPairs = new Set();
    const computed = [];

    widgetItemIds.forEach(itemId => {
      const item = items?.[itemId];
      const links = Array.isArray(item?.links) ? item.links : [];
      links.forEach(linkObj => {
        const targetId = getId(linkObj);
        if (!targetId || targetId === itemId || !widgetItemIds.has(targetId)) {
          return;
        }
        const fromNodeId = toMapWidgetNodeId(itemId);
        const toNodeId = toMapWidgetNodeId(targetId);
        const pairKey = getPairKey(fromNodeId, toNodeId);
        if (manualPairs.has(pairKey) || seenPairs.has(pairKey)) {
          return;
        }
        seenPairs.add(pairKey);
        computed.push({
          id: `auto_${pairKey}`,
          from: fromNodeId,
          to: toNodeId,
          color: 'contrast',
          arrows: 'none',
          curveOffset: 0,
          auto: true,
        });
      });
    });

    return computed;
  }, [displayedLinks, items, visibleMapWidgetsList]);

  useBoardSelectionSync({
    value: {
      hasSelection: selectedLinks.length > 0,
      color: selectedLinks[0]?.color || 'secondary',
      arrows: selectedLinks[0]?.arrows || 'none',
      borderStyle: getStrokeBorderStyle(selectedLinks[0]),
    },
    handlers: {
      hasSelection: onLineSelectionChange,
      color: onSelectedLineColorChange,
      arrows: onSelectedLineArrowsChange,
      borderStyle: onSelectedLineBorderStyleChange,
    },
  });

  useBoardSelectionSync({
    value: {
      hasSelection: selectedPins.length > 0,
      color: selectedPins[0]?.color || 'secondary',
      size: selectedPins[0]?.size || 'md',
      iconSlug: selectedPins[0]?.iconSlug || null,
    },
    handlers: {
      hasSelection: onPinSelectionChange,
      color: onSelectedPinColorChange,
      size: onSelectedPinSizeChange,
      iconSlug: onSelectedPinIconChange,
    },
  });

  useBoardSelectionSync({
    value: {
      hasSelection: selectedShapes.length > 0,
      type: selectedShapes[0]?.type || 'circle',
      color: selectedShapes[0]?.stroke || 'secondary',
      borderStyle: getStrokeBorderStyle(selectedShapes[0]),
    },
    handlers: {
      hasSelection: onShapeSelectionChange,
      type: onSelectedShapeTypeChange,
      color: onSelectedShapeColorChange,
      borderStyle: onSelectedShapeBorderStyleChange,
    },
  });
  useBoardSelectionSync({
    value: {
      hasSelection: selectedShapes.length > 0,
    },
    handlers: {
      hasSelection: onShapeLayerSelectionChange,
    },
  });

  useBoardSelectionSync({
    value: {
      hasSelection: selectedRegions.length > 0,
      color: selectedRegions[0]?.stroke || selectedRegions[0]?.fill || 'secondary',
      borderStyle: getStrokeBorderStyle(selectedRegions[0]),
    },
    handlers: {
      hasSelection: onRegionSelectionChange,
      color: onSelectedRegionColorChange,
      borderStyle: onSelectedRegionBorderStyleChange,
    },
  });

  useBoardSelectionSync({
    value: {
      mode: linkActionMode,
    },
    handlers: {
      mode: onLinkActionChange,
    },
  });
  useBoardSelectionSync({
    value: {
      canCreate: Boolean(
        !activeGroupId &&
          selectedWidgetIds.length +
            selectedShapeIds.length +
            selectedPinIds.length +
            selectedRegionIds.length +
            selectedDrawingIds.length >
            1,
      ),
      canEnter: Boolean(selectedGroupId && !activeGroupId),
      canUngroup: Boolean(selectedGroupId),
      inside: Boolean(activeGroupId),
      activeGroupId: activeGroupId || null,
      activeGroupLabel: activeGroup?.label || '',
    },
    handlers: {
      canCreate: onCreateGroupAvailabilityChange,
      canEnter: onEnterGroupAvailabilityChange,
      canUngroup: onUngroupAvailabilityChange,
      inside: onInsideGroupChange,
      activeGroupId: onActiveGroupIdChange,
      activeGroupLabel: onActiveGroupLabelChange,
    },
  });

  useBoardSelectionSync({
    value: {
      hasSelection: selectedDrawings.length > 0,
      color: selectedDrawings[0]?.stroke || 'secondary',
      borderStyle: getStrokeBorderStyle(selectedDrawings[0]),
    },
    handlers: {
      hasSelection: onDrawingSelectionChange,
      color: onSelectedDrawingColorChange,
      borderStyle: onSelectedDrawingBorderStyleChange,
    },
  });

  useEffect(() => {
    onSelectionCountsChange?.({
      pins: selectedPinIds.length,
      links: selectedLinkIds.length,
      shapes: selectedShapeIds.length,
      regions: selectedRegionIds.length,
      drawings: selectedDrawingIds.length,
      widgets: selectedWidgetIds.length,
      groups: selectedGroupIds.length,
    });
  }, [
    onSelectionCountsChange,
    selectedDrawingIds.length,
    selectedGroupIds.length,
    selectedLinkIds.length,
    selectedPinIds.length,
    selectedRegionIds.length,
    selectedShapeIds.length,
    selectedWidgetIds.length,
  ]);

  const fetchItemById = useCallback(
    async id => {
      if (!id || !/^[a-f0-9]{24}$/i.test(id)) {
        return null;
      }
      const existing = items?.[id];
      if (existing && existing.__partial !== true) {
        return existing;
      }
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${id}`);
        const fetched = res?.data || null;
        if (fetched?._id) {
          dispatch(saveAll({ item: fetched }));
          return fetched;
        }
      } catch (_error) {
        return null;
      }
      return null;
    },
    [dispatch, items],
  );

  const openLinkedItem = useCallback(
    async itemId => {
      if (!itemId) {
        return;
      }
      const targetItem = await fetchItemById(itemId);
      if (!targetItem) {
        return;
      }
      const campaign = targetItem?.campaign
        ? await fetchItemById(targetItem.campaign)
        : undefined;
      const world = targetItem?.world
        ? await fetchItemById(targetItem.world)
        : undefined;
      const universe = targetItem?.universe
        ? await fetchItemById(targetItem.universe)
        : undefined;
      const canOpen = canAccessItem(targetItem, campaign, world, universe);
      if (!canOpen) {
        return;
      }
      dispatch(setDrawerOpen(itemId));
    },
    [dispatch, fetchItemById],
  );

  const {
    updateLinkPatch,
    updatePinPatch,
    updateShapePatch,
    updateRegionPatch,
    deletePins,
    deleteGroup,
    deleteSelectedEntities,
    finalizeRegionDraft,
  } = useBoardMapEntityCommands({
    dispatch,
    tab,
    mapState,
    persistMapField,
    recordBoardHistorySnapshot,
    clearSelection,
    setWidgetLayoutOverrides,
    displayedPins,
    displayedLinks,
    setDisplayedPins,
    setDisplayedLinks,
    displayedShapes,
    setDisplayedShapes,
    displayedRegions,
    setDisplayedRegions,
    displayedDrawings,
    setDisplayedDrawings,
    displayedGroups,
    setDisplayedGroups,
    activeGroupId,
    setActiveGroupId,
    selectedPin,
    selectedShape,
    selectedRegion,
    selectedPins,
    selectedLinks,
    selectedRegions,
    selectedShapes,
    selectedDrawings,
    selectedPinIds,
    selectedLinkIds,
    selectedShapeIds,
    selectedRegionIds,
    selectedDrawingIds,
    selectedWidgetIds,
    attachMapNodeToActiveGroup,
    selectRegionOnly,
    regionDraftPoints,
    setRegionDraftPoints,
    setRegionDraftHoverPoint,
    pinLinkModalId,
    backgroundModalId,
    pinColorCommand,
    pinColorCommandTick,
    pinSizeCommand,
    pinSizeCommandTick,
    pinIconCommand,
    pinIconCommandTick,
    unlinkItemCommandTick,
    openLinkModalCommandTick,
    openBackgroundModalCommandTick,
    shapeTypeCommand,
    shapeTypeCommandTick,
    shapeColorCommand,
    shapeColorCommandTick,
    shapeLayerCommand,
    shapeLayerCommandTick,
    regionColorCommand,
    regionColorCommandTick,
    regionBorderStyleCommand,
    regionBorderStyleCommandTick,
    drawingColorCommand,
    drawingColorCommandTick,
    drawingBorderStyleCommand,
    drawingBorderStyleCommandTick,
    lineColorCommand,
    lineColorCommandTick,
    lineArrowsCommand,
    lineArrowsCommandTick,
    lastHandledPinColorCommandTickRef,
    lastHandledPinSizeCommandTickRef,
    lastHandledPinIconCommandTickRef,
    lastHandledUnlinkItemCommandTickRef,
    lastHandledOpenLinkModalCommandTickRef,
    lastHandledOpenBackgroundModalCommandTickRef,
    lastHandledShapeTypeCommandTickRef,
    lastHandledShapeColorCommandTickRef,
    lastHandledShapeLayerCommandTickRef,
    lastHandledRegionColorCommandTickRef,
    lastHandledRegionBorderStyleCommandTickRef,
    lastHandledDrawingColorCommandTickRef,
    lastHandledDrawingBorderStyleCommandTickRef,
    lastHandledLineColorCommandTickRef,
    lastHandledLineArrowsCommandTickRef,
  });

  const openGridSettingsModal = useCallback(() => {
    dispatch(setOpenModales({ id: gridSettingsModalId, open: true }));
  }, [dispatch, gridSettingsModalId]);

  useBoardCommandTick({
    tick: openGridSettingsModalCommandTick,
    lastHandledTickRef: lastHandledOpenGridSettingsModalCommandTickRef,
    onConsume: openGridSettingsModal,
  });

  const updateGridSettings = useCallback(
    async patch => {
      const nextGrid = {
        ...(mapState?.grid || {}),
        ...(patch || {}),
      };
      nextGrid.visible = nextGrid.visible === true;
      nextGrid.size = Math.max(4, Math.round(Number(nextGrid.size) || 50));
      nextGrid.color = String(nextGrid.color || 'contrast').trim() || 'contrast';
      nextGrid.opacity = Number.isFinite(Number(nextGrid.opacity))
        ? Math.max(0, Math.min(1, Number(nextGrid.opacity)))
        : 0.16;
      setDisplayedGrid(nextGrid);
      await persistMapField('grid', nextGrid);
    },
    [mapState?.grid, persistMapField],
  );

  useBoardCommandTick({
    tick: toggleGridVisibleCommandTick,
    lastHandledTickRef: lastHandledToggleGridVisibleCommandTickRef,
    onConsume: () => {
      updateGridSettings({ visible: mapState?.grid?.visible !== true });
    },
  });

  const handleToggleGridVisible = useCallback(
    async visible => {
      await updateGridSettings({ visible: visible === true });
    },
    [updateGridSettings],
  );

  const handleGridColorChange = useCallback(
    async color => {
      await updateGridSettings({ color });
    },
    [updateGridSettings],
  );

  const handleGridOpacityPreview = useCallback(
    opacity => {
      const nextOpacity = Number.isFinite(Number(opacity))
        ? Math.max(0, Math.min(1, Number(opacity)))
        : 0.16;
      setDisplayedGrid(prev => ({
        ...(prev || mapState?.grid || {}),
        opacity: nextOpacity,
      }));
    },
    [mapState?.grid],
  );

  const handleGridOpacityCommit = useCallback(
    async opacity => {
      await updateGridSettings({ opacity });
    },
    [updateGridSettings],
  );

  const handleSaveGridSize = useCallback(async () => {
    const size = Number(String(gridSizeInput || '').replace(',', '.'));
    if (!Number.isFinite(size) || size <= 0) {
      setGridSizeInput(String(mapState?.grid?.size || 50));
      return;
    }
    await updateGridSettings({ size });
  }, [gridSizeInput, mapState?.grid?.size, updateGridSettings]);

  const persistBackground = useCallback(
    async nextUrl => {
      const trimmedUrl = `${nextUrl || ''}`.trim();
      setIsBackgroundSaving(true);
      setBackgroundError('');
      try {
        const meta = trimmedUrl ? await getImageMeta(trimmedUrl) : { width: 0, height: 0 };
        await persistMapField('background', {
          ...mapState.background,
          imageId: '',
          url: trimmedUrl,
          width: meta.width || 0,
          height: meta.height || 0,
        });
      } catch (error) {
        setBackgroundError("Impossible de charger l'image");
      } finally {
        setIsBackgroundSaving(false);
      }
    },
    [mapState.background, persistMapField],
  );

  const handleBackgroundUrlSubmit = useCallback(async () => {
    if (!backgroundUrlInput?.trim()) {
      return;
    }
    try {
      setIsBackgroundSaving(true);
      setBackgroundError('');
      const res = await uploadPicFromUrl(backgroundUrlInput.trim());
      const uploadedUrl = res?.data?.url || backgroundUrlInput.trim();
      setBackgroundUrlInput(uploadedUrl);
      await persistBackground(uploadedUrl);
    } catch (error) {
      setBackgroundError("Impossible d'importer l'image depuis l'URL");
      setIsBackgroundSaving(false);
    }
  }, [backgroundUrlInput, persistBackground]);

  const handleBackgroundFileChange = useCallback(
    async e => {
      try {
        setIsBackgroundSaving(true);
        setBackgroundError('');
        const res = await uploadPic(e);
        const uploadedUrl = res?.data?.url;
        if (!uploadedUrl) {
          throw new Error('missing_uploaded_url');
        }
        setBackgroundUrlInput(uploadedUrl);
        await persistBackground(uploadedUrl);
      } catch (error) {
        setBackgroundError("Impossible d'importer l'image");
        setIsBackgroundSaving(false);
      } finally {
        if (e?.target) {
          e.target.value = '';
        }
      }
    },
    [persistBackground],
  );

  const handleClearBackground = useCallback(async () => {
    setBackgroundUrlInput('');
    await persistBackground('');
  }, [persistBackground]);

  const handleBackgroundOpacityPreview = useCallback(
    opacity => {
      const nextOpacity = Number.isFinite(Number(opacity))
        ? Math.max(0, Math.min(1, Number(opacity)))
        : 1;
      setDisplayedBackground(prev => ({
        ...(prev || mapState?.background || {}),
        opacity: nextOpacity,
      }));
    },
    [mapState?.background],
  );

  const handleBackgroundOpacityCommit = useCallback(
    async opacity => {
      const nextOpacity = Number.isFinite(Number(opacity))
        ? Math.max(0, Math.min(1, Number(opacity)))
        : 1;
      const nextBackground = {
        ...(mapState?.background || {}),
        opacity: nextOpacity,
      };
      setDisplayedBackground(nextBackground);
      await persistMapField('background', nextBackground);
    },
    [mapState?.background, persistMapField],
  );

  const closeLinkModal = useCallback(() => {
    dispatch(setOpenModales({ id: pinLinkModalId, open: false }));
  }, [dispatch, pinLinkModalId]);

  const commitSelectedLinkLabel = useCallback(
    async nextLabel => {
      if (!selectedLink?.id) {
        return;
      }
      await updateLinkPatch(selectedLink.id, { label: `${nextLabel || ''}`.trim() });
    },
    [selectedLink?.id, updateLinkPatch],
  );

  const promoteAutoLinkToManual = useCallback(
    async autoLink => {
      if (!autoLink?.from || !autoLink?.to) {
        return;
      }
      const from = String(autoLink.from);
      const to = String(autoLink.to);
      const existing = displayedLinks.find(
        link =>
          link &&
          ((String(link.from) === from && String(link.to) === to) ||
            (String(link.from) === to && String(link.to) === from)),
      );
      if (existing?.id) {
        selectLinkOnly(existing.id);
        return;
      }
      const manualId = `${from}-${to}`;
      const nextLink = {
        id: manualId,
        from,
        to,
        hidden: false,
        type: 'line',
        color: 'secondary',
        arrows: 'none',
        curveOffset: 0,
      };
      const nextLinks = [...displayedLinks, nextLink];
      setDisplayedLinks(nextLinks);
      selectLinkOnly(manualId);
      await persistMapField('links', nextLinks);
    },
    [displayedLinks, persistMapField, selectLinkOnly],
  );

  const enterGroup = useCallback(
    groupId => {
      if (!groupId) {
        return;
      }
      const nextGroup = displayedGroups.find(group => group?.id === groupId);
      if (!nextGroup) {
        return;
      }
      previousViewportRef.current = viewport;
      setActiveGroupId(groupId);
      selectGroupOnly(groupId);
      setViewport(prev => ({
        ...prev,
        x: 80 - ((Number(nextGroup?.x) || 0) + mapRenderMetrics.offsetX) * prev.zoom,
        y: 80 - ((Number(nextGroup?.y) || 0) + mapRenderMetrics.offsetY) * prev.zoom,
      }));
    },
    [displayedGroups, mapRenderMetrics.offsetX, mapRenderMetrics.offsetY, selectGroupOnly, setViewport, viewport],
  );

  const exitGroup = useCallback(() => {
    setActiveGroupId(null);
    clearSelection();
    if (previousViewportRef.current) {
      setViewport(previousViewportRef.current);
      previousViewportRef.current = null;
    }
  }, [clearSelection, setViewport]);

  const createGroupFromSelection = useCallback(async () => {
    if (!tab?._id) {
      return;
    }
    const childIds = [
      ...selectedWidgetIds.map(toMapWidgetNodeId),
      ...selectedShapeIds.map(toMapShapeNodeId),
      ...selectedPinIds.map(toMapPinNodeId),
      ...selectedRegionIds.map(toMapRegionNodeId),
      ...selectedDrawingIds.map(toMapDrawingNodeId),
    ].filter(Boolean);
    if (childIds.length < 2) {
      return;
    }
    const rects = childIds
      .map(nodeId => mapNodeRectById.get(nodeId))
      .filter(Boolean)
      .map(rect => ({
        x: Number(rect?.cx) - Number(rect?.w) / 2,
        y: Number(rect?.cy) - Number(rect?.h) / 2,
        w: Number(rect?.w) || 0,
        h: Number(rect?.h) || 0,
      }));
    if (rects.length < 2) {
      return;
    }
    const minX = Math.min(...rects.map(rect => rect.x));
    const minY = Math.min(...rects.map(rect => rect.y));
    const maxX = Math.max(...rects.map(rect => rect.x + rect.w));
    const maxY = Math.max(...rects.map(rect => rect.y + rect.h));
    const groupId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `group_${Date.now()}`;
    const nextGroups = [
      ...displayedGroups,
      {
        id: groupId,
        label: `Groupe ${displayedGroups.length + 1}`,
        childIds,
        x: minX,
        y: minY,
        w: maxX - minX,
        h: maxY - minY,
        collapsed: true,
        locked: false,
        style: {
          stroke: 'secondary',
          fillOpacity: 0.08,
        },
      },
    ];
    const childIdSet = new Set(childIds);
    const groupNodeId = toMapGroupNodeId(groupId);
    const reroutedLinkPairKeys = new Set();
    const nextLinks = displayedLinks.reduce((acc, link) => {
      if (!link?.id || !link?.from || !link?.to) {
        acc.push(link);
        return acc;
      }
      const from = String(link.from);
      const to = String(link.to);
      const fromInside = childIdSet.has(from);
      const toInside = childIdSet.has(to);

      if (fromInside === toInside) {
        acc.push(link);
        return acc;
      }

      const nextLink = fromInside
        ? { ...link, from: groupNodeId }
        : { ...link, to: groupNodeId };
      const pairKey = getPairKey(nextLink.from, nextLink.to);
      if (reroutedLinkPairKeys.has(pairKey)) {
        return acc;
      }
      reroutedLinkPairKeys.add(pairKey);
      acc.push(nextLink);
      return acc;
    }, []);
    setDisplayedGroups(nextGroups);
    setDisplayedLinks(nextLinks);
    selectGroupOnly(groupId);
    await Promise.all([
      persistMapField('groups', nextGroups),
      persistMapField('links', nextLinks),
    ]);
  }, [
    displayedGroups,
    displayedLinks,
    mapNodeRectById,
    persistMapField,
    selectGroupOnly,
    selectedDrawingIds,
    selectedPinIds,
    selectedRegionIds,
    selectedShapeIds,
    selectedWidgetIds,
    tab?._id,
  ]);

  const commitSelectedGroupLabel = useCallback(
    async (groupId, rawLabel) => {
      if (!groupId) {
        return;
      }
      const nextLabel = String(rawLabel || '').trim() || 'Groupe';
      const nextGroups = displayedGroups.map(group =>
        group?.id === groupId ? { ...group, label: nextLabel } : group,
      );
      setDisplayedGroups(nextGroups);
      if (selectedGroupLabelRef.current) {
        selectedGroupLabelRef.current.textContent = nextLabel;
      }
      await persistMapField('groups', nextGroups);
    },
    [displayedGroups, persistMapField],
  );

  useBoardCommandTick({
    tick: groupCommandTick,
    lastHandledTickRef: lastHandledGroupCommandTickRef,
    onConsume: () => {
      if (groupCommand === 'create') {
        createGroupFromSelection();
        return;
      }
      if (groupCommand === 'enter' && selectedGroupId) {
        enterGroup(selectedGroupId);
        return;
      }
      if (groupCommand === 'exit') {
        exitGroup();
        return;
      }
      if (groupCommand === 'ungroup' && selectedGroupId) {
        deleteGroup([selectedGroupId]);
      }
    },
  });
  useBoardCommandTick({
    tick: toggleMeasureScaleCommandTick,
    lastHandledTickRef: lastHandledToggleMeasureScaleCommandTickRef,
    onConsume: () => {
      if (!toggleMeasureScaleCommand) {
        return;
      }
      handleToggleMeasureScale(toggleMeasureScaleCommand);
    },
  });

  const resetCurveForLink = useCallback(
    async linkId => {
      if (!linkId) {
        return;
      }
      await updateLinkPatch(linkId, { curveOffset: 0 });
    },
    [updateLinkPatch],
  );

  const startCurveDrag = useCallback(
    (event, linkId) => {
      if (event.button !== 0) {
        return;
      }
      const link = displayedLinks.find(entry => entry?.id === linkId);
      if (!link) {
        return;
      }
      const geometry = getLinkGeometry(link, mapNodeRectById, mapRenderMetrics);
      if (!geometry) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      linkCurveDragStateRef.current = {
        linkId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        hasMoved: false,
      };
      selectLinkOnly(linkId);
    },
    [displayedLinks, mapNodeRectById, mapRenderMetrics, selectLinkOnly],
  );

  const handleRegionClick = useCallback(
    region => {
      if (mapTool !== 'select') {
        return;
      }
      if (!region?.id) {
        return;
      }
      if (regionClickTimeoutRef.current) {
        window.clearTimeout(regionClickTimeoutRef.current);
        regionClickTimeoutRef.current = null;
      }
      if (!region.linkedItemId) {
        selectRegionOnly(region.id);
        return;
      }
      regionClickTimeoutRef.current = window.setTimeout(() => {
        openLinkedItem(region.linkedItemId);
        regionClickTimeoutRef.current = null;
      }, 200);
    },
    [mapTool, openLinkedItem, selectRegionOnly],
  );

  const handleRegionDoubleClick = useCallback(
    region => {
      if (!region?.id) {
        return;
      }
      if (regionClickTimeoutRef.current) {
        window.clearTimeout(regionClickTimeoutRef.current);
        regionClickTimeoutRef.current = null;
      }
      selectRegionOnly(region.id);
    },
    [selectRegionOnly],
  );

  const handleShapeClick = useCallback(
    shapeId => {
      if (mapTool !== 'select') {
        return;
      }
      if (!shapeId) {
        return;
      }
      if (didShapeDragRef.current === shapeId) {
        didShapeDragRef.current = null;
        return;
      }
      const shape = displayedShapes.find(entry => entry?.id === shapeId);
      if (!shape) {
        return;
      }
      if (shapeClickTimeoutRef.current) {
        window.clearTimeout(shapeClickTimeoutRef.current);
        shapeClickTimeoutRef.current = null;
      }
      if (!shape.linkedItemId) {
        selectShapeOnly(shape.id);
        return;
      }
      shapeClickTimeoutRef.current = window.setTimeout(() => {
        openLinkedItem(shape.linkedItemId);
        shapeClickTimeoutRef.current = null;
      }, 200);
    },
    [displayedShapes, mapTool, openLinkedItem, selectShapeOnly],
  );

  const handleShapeDoubleClick = useCallback(
    shapeId => {
      if (mapTool !== 'select') {
        return;
      }
      if (!shapeId) {
        return;
      }
      if (shapeClickTimeoutRef.current) {
        window.clearTimeout(shapeClickTimeoutRef.current);
        shapeClickTimeoutRef.current = null;
      }
      selectShapeOnly(shapeId);
    },
    [mapTool, selectShapeOnly],
  );

  const handlePinClick = useCallback(
    pin => {
      if (mapTool !== 'select') {
        return;
      }
      if (!pin?.id) {
        return;
      }
      if (didPinDragRef.current === pin.id) {
        didPinDragRef.current = null;
        return;
      }
      if (pinClickTimeoutRef.current) {
        window.clearTimeout(pinClickTimeoutRef.current);
        pinClickTimeoutRef.current = null;
      }
      if (!pin.linkedItemId) {
        return;
      }
      pinClickTimeoutRef.current = window.setTimeout(() => {
        openLinkedItem(pin.linkedItemId);
        pinClickTimeoutRef.current = null;
      }, 200);
    },
    [mapTool, openLinkedItem],
  );

  const handlePinDoubleClick = useCallback(
    pin => {
      if (mapTool !== 'select') {
        return;
      }
      if (!pin?.id) {
        return;
      }
      if (pinClickTimeoutRef.current) {
        window.clearTimeout(pinClickTimeoutRef.current);
        pinClickTimeoutRef.current = null;
      }
      selectPinOnly(pin.id);
    },
    [mapTool, selectPinOnly],
  );

  const handlePinSelect = useCallback(
    pinId => {
      if (mapTool !== 'select') {
        return;
      }
      selectPinOnly(pinId);
    },
    [mapTool, selectPinOnly],
  );

  const handleDrawingSelect = useCallback(
    drawingId => {
      if (mapTool !== 'select') {
        return;
      }
      selectDrawingOnly(drawingId);
    },
    [mapTool, selectDrawingOnly],
  );

  const handleWidgetSelect = useCallback(
    widgetId => {
      if (mapTool !== 'select') {
        return;
      }
      selectWidgetOnly(widgetId);
    },
    [mapTool, selectWidgetOnly],
  );

  const handleGroupSelect = useCallback(groupId => {
    if (mapTool !== 'select') {
      return;
    }
    if (groupId) {
      selectGroupOnly(groupId);
    }
  }, [mapTool, selectGroupOnly]);

  const handleCanvasClick = useCallback(
    async event => {
      if (isAnyModalOpen) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        (
          target.closest('[data-map-link-root="true"]') ||
          target.closest('[data-map-link-control-handle="true"]') ||
          target.closest('[data-map-link-label-editor="true"]') ||
          target.closest('[data-map-group-root="true"]') ||
          target.closest('[data-map-widget-root="true"]') ||
          target.closest('[data-map-shape-root="true"]') ||
          target.closest('[data-map-region-root="true"]') ||
          target.closest('[data-map-drawing-root="true"]')
        )
      ) {
        return;
      }
      if (didRegionDrawRef.current) {
        didRegionDrawRef.current = false;
        return;
      }
      if (didDrawingRef.current) {
        didDrawingRef.current = false;
        return;
      }
      if (didRegionPointDragRef.current) {
        didRegionPointDragRef.current = false;
        return;
      }
      if (didSelectionDragRef.current) {
        didSelectionDragRef.current = false;
        return;
      }
      if (mapTool === 'region') {
        return;
      }
      if (mapTool === 'shape') {
        return;
      }
      if (mapTool === 'line') {
        return;
      }

      if (mapTool !== 'pin') {
        clearSelection();
        return;
      }
      if (!containerRef.current) {
        return;
      }
      if (didPanRef.current) {
        didPanRef.current = false;
        return;
      }
      if (target instanceof HTMLElement) {
        if (target.closest('button, input, textarea, label')) {
          return;
        }
      }
      const rect = containerRef.current.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;
      const offsetX = Number(mapRenderMetrics?.offsetX) || 0;
      const offsetY = Number(mapRenderMetrics?.offsetY) || 0;
      const x = Math.round((localX - viewport.x) / viewport.zoom - offsetX);
      const y = Math.round((localY - viewport.y) / viewport.zoom - offsetY);
      const nextPin = {
        id:
          typeof crypto !== 'undefined' &&
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `pin_${Date.now()}`,
        x,
        y,
        label: '',
        color: 'secondary',
        icon: 'map-pin',
        iconSlug: null,
        size: 'md',
        linkedItemId: null,
        note: '',
        visible: true,
        locked: false,
      };
      const nextPins = [...mapState.pins, nextPin];
      const nextGroups = await attachMapNodeToActiveGroup({
        nodeId: toMapPinNodeId(nextPin.id),
        rect: {
          x: nextPin.x - 15,
          y: nextPin.y - 30,
          w: 30,
          h: 30,
        },
        persist: false,
      });
      await Promise.all([
        persistMapField('pins', nextPins),
        nextGroups !== displayedGroups
          ? persistMapField('groups', nextGroups)
          : Promise.resolve(),
      ]);
      selectPinOnly(nextPin.id);
    },
    [
      clearSelection,
      isAnyModalOpen,
      mapState.pins,
      mapTool,
      attachMapNodeToActiveGroup,
      displayedGroups,
      persistMapField,
      selectPinOnly,
      mapRenderMetrics?.offsetX,
      mapRenderMetrics?.offsetY,
      viewport.x,
      viewport.y,
      viewport.zoom,
    ],
  );

  useBoardDeleteKey({
    hasSelection:
      selectedLinkIds.length > 0 ||
      selectedPinIds.length > 0 ||
      selectedShapeIds.length > 0 ||
      selectedRegionIds.length > 0 ||
      selectedDrawingIds.length > 0 ||
      selectedWidgetIds.length > 0 ||
      selectedGroupIds.length > 0,
    onDelete: deleteSelectedEntities,
  });

  useEffect(() => {
    const handleKeyDown = event => {
      if (mapTool === 'region') {
        if (event.key === 'Escape') {
          setRegionDraftPoints([]);
          setRegionDraftHoverPoint(null);
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          finalizeRegionDraft();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    finalizeRegionDraft,
    mapTool,
  ]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key !== 'Escape') {
        return;
      }
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      if (mapTool === 'region') {
        return;
      }
      clearSelection();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    clearSelection,
    mapTool,
  ]);

  return (
    <BoardCanvasRoot
      containerRef={containerRef}
      className='w-full h-full relative overflow-hidden bg-primary-d bg-opacity-20'
      onWheel={onWheel}
      onPointerDown={handleCanvasPointerDown}
      onClick={handleCanvasClick}
      canvasWidth={canvasWidth}
      canvasHeight={canvasHeight}
      viewport={viewport}
      canvasChildren={
        <>
          <BoardMapBackgroundLayer
            background={displayedBackground}
            boardWidth={canvasWidth}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
          />
          <BoardMapGridLayer
            grid={displayedGrid}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            viewport={viewport}
            viewportWidth={gridViewportWidth}
            viewportHeight={gridViewportHeight}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
          />
          <BoardMapGroupsLayer
            groups={visibleGroups}
            selectedGroupId={selectedGroupId}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            onSelectGroup={handleGroupSelect}
            onEnterGroup={enterGroup}
            onGroupPointerDown={handleGroupPointerDown}
            selectedGroupLabelRef={selectedGroupLabelRef}
            commitSelectedGroupLabel={commitSelectedGroupLabel}
          />
          <BoardMapLinksLayer
            autoItemLinks={autoItemLinks}
            links={displayedLinks}
            nodeRectById={mapNodeRectById}
            selectedLinkId={selectedLinkId}
            selectedLinkIds={selectedLinkIds}
            onSelectLink={selectLinkOnly}
            drawTool={mapTool}
            lineDraft={lineDraft}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            renderMetrics={mapRenderMetrics}
            selectedLinkLabelRef={selectedLinkLabelRef}
            selectedLinkGeometry={selectedLinkGeometry}
            promoteAutoLinkToManual={promoteAutoLinkToManual}
            commitSelectedLinkLabel={commitSelectedLinkLabel}
            startCurveDrag={startCurveDrag}
            resetCurveForLink={resetCurveForLink}
          />
          <BoardMapMeasurementsLayer
            measurements={displayedMeasurements}
            renderMetrics={mapRenderMetrics}
            activeScales={activeMeasureScales}
            onDeleteMeasurement={handleDeleteMeasurement}
          />
          <BoardMapShapesLayer
            editable={layoutEditable}
            backShapes={backShapes}
            frontShapes={frontShapes}
            shapes={visibleShapes}
            selectedShapeId={selectedShapeId}
            selectedShapeIds={selectedShapeIds}
            onSelectShape={handleShapeClick}
            onShapeDoubleClick={handleShapeDoubleClick}
            onShapePointerDown={handleShapePointerDown}
            onShapeResizePointerDown={handleShapeResizePointerDown}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            shapeDraft={shapeDraft}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            getShapeNodeId={toMapShapeNodeId}
          />
          <BoardMapRegionsLayer
            regions={visibleRegions}
            selectedRegionId={selectedRegionId}
            selectedRegionIds={selectedRegionIds}
            onRegionClick={handleRegionClick}
            onRegionDoubleClick={handleRegionDoubleClick}
            onRegionPointerDown={handleRegionPointerDown}
            onRegionPointPointerDown={handleRegionPointPointerDown}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            regionDraftPoints={regionDraftPoints}
            regionDraftHoverPoint={regionDraftHoverPoint}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            getRegionNodeId={toMapRegionNodeId}
          />
          <BoardMapDrawingsLayer
            drawings={visibleDrawings}
            selectedDrawingId={selectedDrawingId}
            selectedDrawingIds={selectedDrawingIds}
            onSelectDrawing={handleDrawingSelect}
            onDrawingPointerDown={handleDrawingPointerDown}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            drawingDraftPoints={drawingDraftPoints}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            getDrawingNodeId={toMapDrawingNodeId}
          />
          <BoardMapWidgetsLayer
            widgets={visibleMapWidgetsList}
            tab={tab}
            editable={layoutEditable}
            widgetEditable={editable}
            selectedWidgetId={selectedWidgetId}
            selectedWidgetIds={selectedWidgetIds}
            layoutOverrides={widgetLayoutOverrides}
            onSelectWidget={handleWidgetSelect}
            onWidgetDragStart={handleWidgetDragStart}
            onWidgetResizeStart={handleWidgetResizeStart}
            didWidgetDragRef={didWidgetDragRef}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            getWidgetNodeId={toMapWidgetNodeId}
          />
          <BoardMapPinsLayer
            pins={visiblePins}
            selectedPinId={selectedPinId}
            selectedPinIds={selectedPinIds}
            linkedItemLabelsByPinId={linkedItemLabelsByPinId}
            onSelectPin={handlePinSelect}
            onPinPointerDown={handlePinPointerDown}
            onPinClick={handlePinClick}
            onPinDoubleClick={handlePinDoubleClick}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            getPinNodeId={toMapPinNodeId}
          />
        </>
      }
      overlayChildren={
        <>
          <BoardMapDraftLayer
            mapTool={mapTool}
            selectionBox={selectionBox}
          />
          {layoutEditable && isDropActive ? (
            <div className='absolute inset-0 z-10000 pointer-events-none border-2 border-dashed border-secondary bg-secondary bg-opacity-10' />
          ) : null}
          <BoardMapMiniMap
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            viewport={viewport}
            viewportWidth={gridViewportWidth}
            viewportHeight={gridViewportHeight}
            nodeRectById={mapNodeRectById}
            nodeMetaById={mapNodeMetaById}
            offsetX={mapRenderMetrics.offsetX}
            offsetY={mapRenderMetrics.offsetY}
            setViewport={setViewport}
          />
        </>
      }
    >
      <BoardMapModals
        boardId={boardId}
        backgroundModalId={backgroundModalId}
        pinLinkModalId={pinLinkModalId}
        measureSettingsModalId={measureSettingsModalId}
        gridSettingsModalId={gridSettingsModalId}
        backgroundFileInputRef={backgroundFileInputRef}
        mapState={{
          ...mapState,
          background: displayedBackground,
          grid: displayedGrid,
        }}
        gridSizeInput={gridSizeInput}
        setGridSizeInput={setGridSizeInput}
        onToggleGridVisible={handleToggleGridVisible}
        onGridColorChange={handleGridColorChange}
        onGridOpacityPreview={handleGridOpacityPreview}
        onGridOpacityCommit={handleGridOpacityCommit}
        onSaveGridSize={handleSaveGridSize}
        backgroundUrlInput={backgroundUrlInput}
        setBackgroundUrlInput={setBackgroundUrlInput}
        handleBackgroundFileChange={handleBackgroundFileChange}
        handleBackgroundUrlSubmit={handleBackgroundUrlSubmit}
        handleClearBackground={handleClearBackground}
        onBackgroundOpacityPreview={handleBackgroundOpacityPreview}
        onBackgroundOpacityCommit={handleBackgroundOpacityCommit}
        isBackgroundSaving={isBackgroundSaving}
        backgroundError={backgroundError}
        selectedLinkTarget={selectedLinkTarget}
        updatePinPatch={updatePinPatch}
        updateShapePatch={updateShapePatch}
        updateRegionPatch={updateRegionPatch}
        closeLinkModal={closeLinkModal}
        deletePins={deletePins}
        pendingMeasureReference={pendingMeasureReference}
        measureScaleValueInput={measureScaleValueInput}
        setMeasureScaleValueInput={setMeasureScaleValueInput}
        measureScaleUnitInput={measureScaleUnitInput}
        setMeasureScaleUnitInput={setMeasureScaleUnitInput}
        measureScaleError={measureScaleError}
        measureScales={measureScales}
        activeMeasureScaleIds={activeMeasureScaleIds}
        onSaveMeasureScale={handleSaveMeasureScale}
        onToggleMeasureScale={handleToggleMeasureScale}
        onMultiplyMeasureScale={handleMultiplyMeasureScale}
        onUpdateMeasureScale={handleUpdateMeasureScale}
        onCloseMeasureSettingsAndTakeMeasure={handleCloseMeasureSettingsAndTakeMeasure}
        onDeleteMeasureScale={handleDeleteMeasureScale}
      />
    </BoardCanvasRoot>
  );
};
